<template>
  <section class="dynamic-venue" :class="{ 'has-selection': hasSelection, 'is-section-focused': activeSection, 'has-zone-picker': zonePickerSection, 'has-stage-modal': stagePickerSection }">
    <div v-if="loading" class="dynamic-venue__loading">
      <div class="dynamic-venue__loader-ring"></div>
      <strong>Armando el venue...</strong>
      <small>Cargando zonas, entradas y disponibilidad.</small>
    </div>

    <div v-else-if="loadError" class="dynamic-venue__error">
      <strong>No pudimos abrir el Venue Dinámico</strong>
      <p>{{ loadError }}</p>
      <button class="dynamic-venue__primary" type="button" @click="$emit('fallback')">Usar checkout normal</button>
    </div>

    <div v-else class="dynamic-venue__grid">
      <div class="dynamic-venue__map-card">
        <div class="dynamic-venue__map-toolbar">
          <div class="dynamic-venue__breadcrumb">
            <button type="button" @click="goOverview">Vista general</button>
            <span v-if="activeSection">/ {{ activeSection.name }}</span>
            <small>{{ activeSection ? activeSection.description : 'Toca una zona, haz zoom y elige asiento exacto.' }}</small>
          </div>
          <div class="dynamic-venue__legend">
            <span><i class="is-available"></i>Disponible</span>
            <span><i class="is-selected"></i>Seleccionado</span>
            <span><i class="is-occupied"></i>No disponible</span>
          </div>
        </div>

        <div class="dynamic-venue__zone-strip" aria-label="Localidades del venue">
          <button
            v-for="section in sections"
            :key="`strip-${section.id}`"
            type="button"
            :class="{ 'is-active': activeSection && activeSection.id === section.id }"
            :disabled="!section.is_configured"
            @click="handleSectionClick(section)"
          >
            <span>{{ section.name }}</span>
            <strong>{{ sectionPriceLabel(section) }}</strong>
          </button>
        </div>

        <div class="dynamic-venue__stage" :class="{ 'is-dragging': isDragging, 'is-pinching': isPinching, 'is-section-focused': activeSection, 'has-selection': hasSelection }">
          <svg
            ref="venueSvg"
            class="dynamic-venue__svg"
            :viewBox="viewBoxString"
            role="img"
            aria-label="Mapa dinámico de asientos"
            @pointerdown="beginMapInteraction"
            @pointermove="moveMapInteraction"
            @pointerup="endMapInteraction"
            @pointercancel="endMapInteraction"
            @wheel.prevent="handleWheelZoom"
            @dblclick.prevent="handleDoubleClickZoom"
          >
            <defs>
              <filter id="dynamicVenueGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <linearGradient id="dynamicVenueRing" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#f4f8ff" />
                <stop offset="46%" stop-color="#dce5fb" />
                <stop offset="100%" stop-color="#8e6bc4" />
              </linearGradient>
              <linearGradient id="dynamicVenueFloorDepth" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#202837" />
                <stop offset="42%" stop-color="#101722" />
                <stop offset="100%" stop-color="#04070d" />
              </linearGradient>
              <linearGradient id="dynamicVenueFloorSheen" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="rgba(255,255,255,0.2)" />
                <stop offset="34%" stop-color="rgba(220,229,251,0.06)" />
                <stop offset="100%" stop-color="rgba(255,255,255,0)" />
              </linearGradient>
              <radialGradient id="dynamicVenueFloorVignette" cx="50%" cy="48%" r="72%">
                <stop offset="0%" stop-color="rgba(0,0,0,0)" />
                <stop offset="62%" stop-color="rgba(0,0,0,0.08)" />
                <stop offset="100%" stop-color="rgba(0,0,0,0.54)" />
              </radialGradient>
              <linearGradient id="dynamicVenueZoneSheen" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="rgba(255,255,255,0.28)" />
                <stop offset="42%" stop-color="rgba(255,255,255,0.05)" />
                <stop offset="100%" stop-color="rgba(255,255,255,0)" />
              </linearGradient>
              <linearGradient id="dynamicVenueSeatAvailable" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#f7fbff" />
                <stop offset="100%" stop-color="#aebbd0" />
              </linearGradient>
              <linearGradient id="dynamicVenueSeatSelected" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#f8f5ff" />
                <stop offset="52%" stop-color="#b19de4" />
                <stop offset="100%" stop-color="#7054b8" />
              </linearGradient>
              <pattern id="dynamicVenueGrid" width="90" height="90" patternUnits="userSpaceOnUse">
                <path d="M 90 0 L 0 0 0 90" fill="none" stroke="rgba(220, 230, 248, 0.055)" stroke-width="1" />
              </pattern>
              <marker id="dynamicVenueArrow" markerWidth="16" markerHeight="16" refX="13" refY="8" orient="auto" markerUnits="strokeWidth">
                <path d="M2,2 L14,8 L2,14 Z" fill="#dce5fb" />
              </marker>
            </defs>

            <g class="dynamic-venue__viewport" :transform="pinchPreviewTransform || null">
              <rect class="dynamic-venue__floor" :x="floorBox.x" :y="floorBox.y" :width="floorBox.width" :height="floorBox.height" rx="28" />
              <rect class="dynamic-venue__floor-grid" :x="floorBox.x" :y="floorBox.y" :width="floorBox.width" :height="floorBox.height" rx="28" />
              <rect class="dynamic-venue__floor-sheen" :x="floorBox.x" :y="floorBox.y" :width="floorBox.width" :height="floorBox.height" rx="28" />
              <rect class="dynamic-venue__floor-vignette" :x="floorBox.x" :y="floorBox.y" :width="floorBox.width" :height="floorBox.height" rx="28" />

              <g class="dynamic-venue__guides" aria-hidden="true">
                <line :x1="guideMetrics.cx" :y1="floorBox.y + 30" :x2="guideMetrics.cx" :y2="floorBox.y + floorBox.height - 30" />
                <line :x1="floorBox.x + 60" :y1="guideMetrics.cy" :x2="floorBox.x + floorBox.width - 60" :y2="guideMetrics.cy" />
                <circle :cx="guideMetrics.cx" :cy="guideMetrics.cy" :r="guideMetrics.innerRadius" />
                <circle :cx="guideMetrics.cx" :cy="guideMetrics.cy" :r="guideMetrics.outerRadius" />
              </g>

              <g class="dynamic-venue__layers">
                <template v-for="item in mapRenderItems" :key="`map-layer-${item.key}`">
                <g
                  v-for="section in item.type === 'section' ? [item.element] : []"
                  :key="section.id"
                  class="dynamic-venue__zone"
                  :class="{ 'is-active': activeSection && activeSection.id === section.id, 'is-muted': activeSection && activeSection.id !== section.id, 'is-unconfigured': !section.is_configured, 'has-zone-selection': isZoneSection(section) && sectionZoneQuantity(section) > 0 }"
                  :style="depthStyle(section, 'section')"
                  @pointerdown="beginSectionTap(section)"
                >
                  <template v-for="area in sectionVisualAreas(section)" :key="area.key">
                  <template v-if="sectionAreaShapeType(area) === 'path'">
                    <path
                      class="dynamic-venue__zone-aura dynamic-venue__zone-aura--path"
                      :d="sectionAreaPath(area)"
                      :stroke="sectionTone(section)"
                      :fill-rule="sectionAreaFillRule(area)"
                      :clip-rule="sectionAreaFillRule(area)"
                    />
                    <path
                      :d="sectionAreaPath(area)"
                      :fill="sectionFill(section)"
                      :stroke="sectionTone(section)"
                      :fill-rule="sectionAreaFillRule(area)"
                      :clip-rule="sectionAreaFillRule(area)"
                    />
                    <path
                      class="dynamic-venue__zone-sheen"
                      :d="sectionAreaPath(area)"
                      :fill-rule="sectionAreaFillRule(area)"
                      :clip-rule="sectionAreaFillRule(area)"
                    />
                  </template>
                  <template v-else-if="sectionAreaShapeType(area) === 'ellipse'">
                    <ellipse
                      class="dynamic-venue__zone-aura"
                      :cx="sectionAreaCenterX(area)"
                      :cy="sectionAreaCenterY(area)"
                      :rx="(area.shape.w / 2) + 8"
                      :ry="(area.shape.h / 2) + 8"
                      :stroke="sectionTone(section)"
                    />
                    <ellipse
                      :cx="sectionAreaCenterX(area)"
                      :cy="sectionAreaCenterY(area)"
                      :rx="area.shape.w / 2"
                      :ry="area.shape.h / 2"
                      :fill="sectionFill(section)"
                      :stroke="sectionTone(section)"
                    />
                    <ellipse
                      class="dynamic-venue__zone-sheen"
                      :cx="sectionAreaCenterX(area)"
                      :cy="sectionAreaCenterY(area)"
                      :rx="area.shape.w / 2"
                      :ry="area.shape.h / 2"
                    />
                  </template>
                  <template v-else-if="sectionAreaShapeType(area) === 'polygon'">
                    <polygon
                      class="dynamic-venue__zone-aura dynamic-venue__zone-aura--polygon"
                      :points="sectionAreaPolygonPoints(area)"
                      :stroke="sectionTone(section)"
                    />
                    <polygon
                      :points="sectionAreaPolygonPoints(area)"
                      :fill="sectionFill(section)"
                      :stroke="sectionTone(section)"
                    />
                    <polygon
                      class="dynamic-venue__zone-sheen"
                      :points="sectionAreaPolygonPoints(area)"
                    />
                  </template>
                  <template v-else>
                    <rect
                      class="dynamic-venue__zone-aura"
                      :x="area.shape.x - 8"
                      :y="area.shape.y - 8"
                      :width="area.shape.w + 16"
                      :height="area.shape.h + 16"
                      :rx="(area.shape.rx || 28) + 8"
                      :stroke="sectionTone(section)"
                    />
                    <rect
                      :x="area.shape.x"
                      :y="area.shape.y"
                      :width="area.shape.w"
                      :height="area.shape.h"
                      :rx="area.shape.rx || 28"
                      :fill="sectionFill(section)"
                      :stroke="sectionTone(section)"
                    />
                    <rect
                      class="dynamic-venue__zone-sheen"
                      :x="area.shape.x"
                      :y="area.shape.y"
                      :width="area.shape.w"
                      :height="area.shape.h"
                      :rx="area.shape.rx || 28"
                    />
                  </template>
                  </template>
                  <text v-if="shouldShowSectionLabel(section)" :x="sectionLabelX(section)" :y="sectionLabelY(section)" text-anchor="middle">
                    {{ section.name }}
                  </text>
                  <text v-if="shouldShowSectionLabel(section)" class="dynamic-venue__zone-price" :x="sectionPriceX(section)" :y="sectionPriceY(section)" text-anchor="middle">
                    {{ sectionPriceLabel(section) }}
                  </text>
                </g>
                <g v-for="object in item.type === 'object' ? [item.element] : []" :key="object.id" class="dynamic-venue__object" :style="depthStyle(object, 'object')">
                <g v-if="object.type === 'ring'" class="dynamic-venue__object-ring">
                  <rect class="dynamic-venue__object-ring-shadow" :x="object.x - 18" :y="object.y - 18" :width="object.w + 36" :height="object.h + 36" rx="26" />
                  <rect class="dynamic-venue__object-ring-floor" :x="object.x" :y="object.y" :width="object.w" :height="object.h" rx="18" />
                  <rect class="dynamic-venue__object-ring-inner" :x="object.x + 18" :y="object.y + 18" :width="object.w - 36" :height="object.h - 36" rx="12" />
                  <line :x1="object.x + 26" :x2="object.x + object.w - 26" :y1="object.y + 54" :y2="object.y + 54" />
                  <line :x1="object.x + 26" :x2="object.x + object.w - 26" :y1="object.y + object.h - 54" :y2="object.y + object.h - 54" />
                  <line :x1="object.x + 54" :x2="object.x + 54" :y1="object.y + 26" :y2="object.y + object.h - 26" />
                  <line :x1="object.x + object.w - 54" :x2="object.x + object.w - 54" :y1="object.y + 26" :y2="object.y + object.h - 26" />
                </g>
                <g v-else-if="objectIsSemantic(object)" class="dynamic-venue__semantic-object" :class="`is-${objectSemanticType(object)}`" :style="{ '--object-color': objectColor(object), '--object-fill': objectFill(object) }">
                  <template v-if="objectSemanticType(object) === 'stage'">
                    <ellipse
                      v-if="objectStageVariant(object) === 'arena'"
                      class="dynamic-venue__semantic-fill"
                      :cx="object.x + object.w / 2"
                      :cy="object.y + object.h / 2"
                      :rx="object.w * 0.43"
                      :ry="object.h * 0.36"
                    />
                    <polygon v-else class="dynamic-venue__semantic-fill" :points="objectStageDeckPoints(object)" />
                    <line v-if="objectStageVariant(object) === 'front'" class="dynamic-venue__semantic-line" :x1="object.x + object.w * 0.16" :x2="object.x + object.w * 0.84" :y1="object.y + object.h * 0.72" :y2="object.y + object.h * 0.72" />
                    <path v-else-if="objectStageVariant(object) === 'runway'" class="dynamic-venue__semantic-line is-thin" :d="objectStageRunwayLinePath(object)" />
                    <path v-else-if="objectStageVariant(object) === 'corner'" class="dynamic-venue__semantic-line is-thin" :d="objectStageCornerLinePath(object)" />
                    <ellipse v-else-if="objectStageVariant(object) === 'arena'" class="dynamic-venue__semantic-line is-thin" :cx="object.x + object.w / 2" :cy="object.y + object.h / 2" :rx="object.w * 0.26" :ry="object.h * 0.2" />
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'entrance' || objectSemanticType(object) === 'exit'">
                    <path class="dynamic-venue__semantic-stroke-fill" :d="objectPortalPath(object)" />
                    <line class="dynamic-venue__semantic-line" :x1="object.x + object.w * 0.3" :x2="object.x + object.w * 0.72" :y1="object.y + object.h * 0.62" :y2="object.y + object.h * 0.62" marker-end="url(#dynamicVenueArrow)" />
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'bathroom'">
                    <template v-if="objectBathroomVariant(object) === 'accessible'">
                      <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.35" :cy="object.y + object.h * 0.67" :r="object.h * 0.15" />
                      <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.38" :cy="object.y + object.h * 0.28" :r="object.h * 0.1" />
                      <path class="dynamic-venue__semantic-stroke" :d="objectBathroomAccessiblePath(object)" />
                    </template>
                    <template v-else-if="objectBathroomVariant(object) === 'women'">
                      <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.34" :cy="object.y + object.h * 0.25" :r="object.h * 0.1" />
                      <path class="dynamic-venue__semantic-stroke-fill" :d="objectBathroomWomenPath(object)" />
                    </template>
                    <template v-else-if="objectBathroomVariant(object) === 'men'">
                      <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.34" :cy="object.y + object.h * 0.25" :r="object.h * 0.1" />
                      <path class="dynamic-venue__semantic-stroke" :d="objectBathroomMenPath(object)" />
                    </template>
                    <template v-else>
                      <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.28" :cy="object.y + object.h * 0.28" :r="object.h * 0.1" />
                      <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.42" :cy="object.y + object.h * 0.28" :r="object.h * 0.1" />
                      <path class="dynamic-venue__semantic-stroke" :d="objectBathroomMixedPath(object)" />
                    </template>
                    <text class="dynamic-venue__semantic-text" :x="object.x + object.w * 0.7" :y="object.y + object.h * 0.63" text-anchor="middle">{{ objectBathroomText(object) }}</text>
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'bar'">
                    <path class="dynamic-venue__semantic-stroke-fill" :d="`M ${object.x + object.w * 0.14} ${object.y + object.h * 0.58} L ${object.x + object.w * 0.86} ${object.y + object.h * 0.58} L ${object.x + object.w * 0.74} ${object.y + object.h * 0.82} L ${object.x + object.w * 0.26} ${object.y + object.h * 0.82} Z`" />
                    <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.38" :cy="object.y + object.h * 0.32" :r="object.h * 0.1" />
                    <circle class="dynamic-venue__semantic-fill" :cx="object.x + object.w * 0.62" :cy="object.y + object.h * 0.32" :r="object.h * 0.1" />
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'dj'">
                    <rect class="dynamic-venue__semantic-stroke-fill" :x="object.x + object.w * 0.14" :y="object.y + object.h * 0.48" :width="object.w * 0.72" :height="object.h * 0.34" :rx="object.h * 0.08" />
                    <circle class="dynamic-venue__semantic-stroke-fill" :cx="object.x + object.w * 0.34" :cy="object.y + object.h * 0.38" :r="object.h * 0.18" />
                    <circle class="dynamic-venue__semantic-stroke-fill" :cx="object.x + object.w * 0.66" :cy="object.y + object.h * 0.38" :r="object.h * 0.18" />
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'screen'">
                    <rect class="dynamic-venue__semantic-stroke-fill" :x="object.x + object.w * 0.1" :y="object.y + object.h * 0.14" :width="object.w * 0.8" :height="object.h * 0.48" :rx="object.h * 0.06" />
                    <line class="dynamic-venue__semantic-line is-thin" :x1="object.x + object.w * 0.5" :x2="object.x + object.w * 0.5" :y1="object.y + object.h * 0.62" :y2="object.y + object.h * 0.84" />
                    <line class="dynamic-venue__semantic-line is-thin" :x1="object.x + object.w * 0.34" :x2="object.x + object.w * 0.66" :y1="object.y + object.h * 0.84" :y2="object.y + object.h * 0.84" />
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'security'">
                    <path class="dynamic-venue__semantic-stroke-fill" :d="objectShieldPath(object)" />
                    <path class="dynamic-venue__semantic-line is-thin" :d="`M ${object.x + object.w * 0.5} ${object.y + object.h * 0.26} V ${object.y + object.h * 0.7} M ${object.x + object.w * 0.36} ${object.y + object.h * 0.47} H ${object.x + object.w * 0.64}`" />
                  </template>
                  <path v-else-if="objectSemanticType(object) === 'stairs'" class="dynamic-venue__semantic-line" :d="objectStairsPath(object)" />
                  <template v-else-if="objectSemanticType(object) === 'emergency'">
                    <circle class="dynamic-venue__semantic-stroke-fill" :cx="object.x + object.w * 0.5" :cy="object.y + object.h * 0.5" :r="Math.min(object.w, object.h) * 0.32" />
                    <path class="dynamic-venue__semantic-line" :d="`M ${object.x + object.w * 0.5} ${object.y + object.h * 0.3} V ${object.y + object.h * 0.7} M ${object.x + object.w * 0.32} ${object.y + object.h * 0.5} H ${object.x + object.w * 0.68}`" />
                  </template>
                  <circle v-else-if="objectSemanticType(object) === 'column'" class="dynamic-venue__semantic-stroke-fill" :cx="object.x + object.w * 0.5" :cy="object.y + object.h * 0.5" :r="Math.min(object.w, object.h) * 0.36" />
                  <template v-else-if="objectSemanticType(object) === 'sofa' || objectSemanticType(object) === 'booth'">
                    <rect class="dynamic-venue__semantic-stroke-fill" :x="object.x + object.w * 0.1" :y="object.y + object.h * 0.34" :width="object.w * 0.8" :height="object.h * 0.42" :rx="object.h * 0.16" />
                    <rect class="dynamic-venue__semantic-fill" :x="object.x + object.w * 0.16" :y="object.y + object.h * 0.2" :width="object.w * 0.68" :height="object.h * 0.28" :rx="object.h * 0.13" />
                    <line class="dynamic-venue__semantic-line is-thin" :x1="object.x + object.w * 0.32" :x2="object.x + object.w * 0.32" :y1="object.y + object.h * 0.38" :y2="object.y + object.h * 0.72" />
                    <line class="dynamic-venue__semantic-line is-thin" :x1="object.x + object.w * 0.68" :x2="object.x + object.w * 0.68" :y1="object.y + object.h * 0.38" :y2="object.y + object.h * 0.72" />
                  </template>
                  <template v-else-if="objectSemanticType(object) === 'production' || objectSemanticType(object) === 'box-office'">
                    <rect class="dynamic-venue__semantic-stroke-fill" :x="object.x + object.w * 0.16" :y="object.y + object.h * 0.24" :width="object.w * 0.68" :height="object.h * 0.52" :rx="object.h * 0.08" />
                    <line class="dynamic-venue__semantic-line is-thin" :x1="object.x + object.w * 0.3" :x2="object.x + object.w * 0.7" :y1="object.y + object.h * 0.5" :y2="object.y + object.h * 0.5" />
                  </template>
                  <text v-if="objectShouldShowLabel(object)" class="dynamic-venue__semantic-label" :x="object.x + object.w / 2" :y="object.y + object.h + 24" text-anchor="middle">{{ object.label }}</text>
                </g>
                <ellipse
                  v-else-if="objectShapeType(object) === 'ellipse'"
                  class="dynamic-venue__object-shape"
                  :cx="object.x + object.w / 2"
                  :cy="object.y + object.h / 2"
                  :rx="object.w / 2"
                  :ry="object.h / 2"
                  :fill="objectFill(object)"
                  :stroke="objectColor(object)"
                />
                <line
                  v-else-if="objectShapeType(object) === 'line' || objectShapeType(object) === 'arrow'"
                  class="dynamic-venue__object-line"
                  :x1="object.x"
                  :x2="object.x + object.w"
                  :y1="object.y + object.h / 2"
                  :y2="object.y + object.h / 2"
                  :stroke="objectColor(object)"
                  :marker-end="objectShapeType(object) === 'arrow' ? 'url(#dynamicVenueArrow)' : null"
                />
                <text
                  v-else-if="objectShapeType(object) === 'text'"
                  class="dynamic-venue__object-text-only"
                  :x="object.x + object.w / 2"
                  :y="object.y + object.h / 2 + 8"
                  text-anchor="middle"
                  :style="objectTextStyle(object)"
                >{{ object.label }}</text>
                <rect
                  v-else
                  class="dynamic-venue__object-shape"
                  :x="object.x"
                  :y="object.y"
                  :width="object.w"
                  :height="object.h"
                  :rx="object.rx || 14"
                  :fill="objectFill(object)"
                  :stroke="objectColor(object)"
                />
                <text v-if="!objectIsSemantic(object) && objectShouldShowLabel(object)" :x="object.x + object.w / 2" :y="object.y + object.h / 2 + 8" text-anchor="middle">{{ object.label }}</text>
                </g>
                <g
                  v-for="seat in item.type === 'seat' ? [item.element] : []"
                  :key="seat.key"
                  class="dynamic-venue__seat"
                  :class="seatClass(seat)"
                  :style="depthStyle(seat, 'seat')"
                  :transform="seatTransform(seat)"
                  @pointerdown.stop
                  @pointerup.stop="handleSeatClick(seat)"
                  @mouseenter="hoverSeat(seat)"
                  @mouseleave="hoverSeat(null)"
                >
                  <rect class="dynamic-venue__seat-hit" :x="-8" :y="-8" :width="seat.w + 16" :height="seat.h + 16" rx="12" />
                  <rect v-if="isSeatSelected(seat)" class="dynamic-venue__seat-halo" :x="-6" :y="-6" :width="seat.w + 12" :height="seat.h + 12" rx="12" />
                  <template v-if="isTableSeat(seat)">
                    <template v-if="tableStyleVariant(seat) === 'circle'">
                      <circle class="dynamic-venue__table-round" :cx="seat.w / 2" :cy="seat.h / 2" :r="Math.min(seat.w, seat.h) * 0.42" />
                      <text class="dynamic-venue__table-round-label" :x="seat.w / 2" :y="seat.h / 2 + 7" text-anchor="middle">{{ seat.label }}</text>
                    </template>
                    <template v-else>
                      <ellipse class="dynamic-venue__table-aura" :cx="seat.w / 2" :cy="seat.h / 2" :rx="seat.w * 0.54" :ry="seat.h * 0.5" />
                      <circle class="dynamic-venue__table-chair" :cx="seat.w * 0.2" :cy="seat.h * 0.2" :r="Math.min(seat.w, seat.h) * 0.09" />
                      <circle class="dynamic-venue__table-chair" :cx="seat.w * 0.8" :cy="seat.h * 0.2" :r="Math.min(seat.w, seat.h) * 0.09" />
                      <circle class="dynamic-venue__table-chair" :cx="seat.w * 0.16" :cy="seat.h * 0.58" :r="Math.min(seat.w, seat.h) * 0.09" />
                      <circle class="dynamic-venue__table-chair" :cx="seat.w * 0.84" :cy="seat.h * 0.58" :r="Math.min(seat.w, seat.h) * 0.09" />
                      <rect class="dynamic-venue__table-top" :x="seat.w * 0.23" :y="seat.h * 0.22" :width="seat.w * 0.54" :height="seat.h * 0.54" :rx="Math.min(seat.w, seat.h) * 0.16" />
                      <text :x="seat.w / 2" :y="seat.h / 2 + 4" text-anchor="middle">{{ seat.label }}</text>
                    </template>
                  </template>
                  <template v-else>
                    <rect :width="seat.w" :height="seat.h" rx="7" />
                    <text v-if="shouldShowSeatLabel(seat)" :x="seat.w / 2" :y="seat.h / 2 + 4" text-anchor="middle">{{ seat.label }}</text>
                  </template>
                </g>
                </template>
              </g>
            </g>
          </svg>

          <div class="dynamic-venue__zoom-controls" aria-label="Controles de mapa">
            <button type="button" @pointerdown.stop @click.stop="zoomIn"><i class="fas fa-plus"></i></button>
            <button type="button" @pointerdown.stop @click.stop="zoomOut"><i class="fas fa-minus"></i></button>
            <button type="button" @pointerdown.stop @click.stop="resetCamera"><i class="fas fa-expand"></i></button>
          </div>

          <div v-if="previewSeatInfo && !showSeatStageController" class="dynamic-venue__seat-peek">
            <small>{{ previewSeatInfo.selected ? 'Asiento reservado' : 'Vista previa' }}</small>
            <strong>{{ previewSeatInfo.seat.seat_name || previewSeatInfo.seat.label }}</strong>
            <span>{{ previewSeatInfo.ticket ? previewSeatInfo.ticket.name : previewSeatInfo.section?.name }} · {{ previewSeatInfo.ticket ? formatMoney(ticketGrossAmount(previewSeatInfo.ticket)) : 'No disponible' }}<template v-if="previewSeatInfo.ticket && ticketQuantityFactor(previewSeatInfo.ticket) > 1"> · {{ ticketAccessLabel(previewSeatInfo.ticket) }}</template></span>
          </div>

          <div v-if="showSeatStageController" class="dynamic-venue__seat-stage-card" @pointerdown.stop>
            <small>{{ stageChoiceConfirmed(activeSection) ? 'Entrada elegida' : 'Antes de elegir asiento' }}</small>
            <strong>{{ selectedTicketForSection(activeSection)?.name || activeSection.name }}</strong>
            <span>{{ selectedTicketForSection(activeSection) ? `${ticketAccessLabel(selectedTicketForSection(activeSection))} · ${formatMoney(ticketGrossAmount(selectedTicketForSection(activeSection)))}` : 'Elige una entrada para continuar' }}</span>
            <button type="button" @click.stop="openStagePicker(activeSection)">{{ stageChoiceConfirmed(activeSection) ? 'Cambiar entrada' : 'Elegir entrada' }}</button>
          </div>

          <div v-if="zonePickerSection" class="dynamic-venue__zone-picker" role="group" aria-live="polite">
            <small>Compra en {{ zonePickerSection.name }}</small>
            <div class="dynamic-venue__zone-picker-title">
              <strong>{{ selectedTicketForSection(zonePickerSection)?.name || zonePickerSection.name }}</strong>
              <span v-if="zoneQuantity(zonePickerSection) > 0">x{{ zoneQuantity(zonePickerSection) }}</span>
            </div>

            <button v-if="sectionTickets(zonePickerSection).length > 1" class="dynamic-venue__zone-ticket-button" type="button" @pointerdown.stop @click.stop="openStagePicker(zonePickerSection)">
              <span>Cambiar categoría</span>
              <strong>{{ selectedTicketForSection(zonePickerSection)?.name }}</strong>
              <i class="fas fa-chevron-up"></i>
            </button>

            <span>{{ zonePickerSummary }}</span>
            <div class="dynamic-venue__zone-stepper">
              <button type="button" :aria-label="`Restar unidad de ${zonePickerSection.name}`" @pointerdown.stop @click.stop="decrementZone(zonePickerSection)"><i class="fas fa-minus"></i></button>
              <b aria-live="polite">{{ zoneQuantity(zonePickerSection) }}</b>
              <button type="button" :aria-label="`Sumar unidad de ${zonePickerSection.name}`" @pointerdown.stop @click.stop="incrementZone(zonePickerSection)"><i class="fas fa-plus"></i></button>
            </div>
            <button class="dynamic-venue__zone-continue" type="button" :disabled="!canContinue || holding" @pointerdown.stop @click.stop="continueCheckout">
              <span>{{ continueButtonLabel }}</span>
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>

          <p v-if="holdError" class="dynamic-venue__hold-toast">
            <i class="fas fa-circle-exclamation"></i>
            <span>{{ holdError }}</span>
          </p>

          <div v-if="!hasSelection" class="dynamic-venue__map-hint">
            <i class="fas fa-crosshairs"></i>
            <span>{{ mapGuidance }}</span>
          </div>

          <div v-if="hasSelection && !zonePickerSection" class="dynamic-venue__mobile-cta">
            <div>
              <small>{{ selectedSelectionSummary }}</small>
              <strong>{{ formatMoney(subtotal) }}</strong>
              <small v-if="taxes > 0" class="dynamic-venue__mobile-tax">Subtotal {{ formatMoney(subtotalBase) }} · {{ taxSummaryLabel }} {{ formatMoney(taxes) }}</small>
            </div>
            <button type="button" :disabled="!canContinue || holding" @pointerdown.stop @click.stop="continueCheckout">
              <span>{{ mobileContinueLabel }}</span>
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <transition name="dynamic-venue-stage-picker">
        <div v-if="stagePickerSection" class="dynamic-venue__stage-modal" role="dialog" aria-modal="true" :aria-label="`Elegir entrada en ${stagePickerSection.name}`" @click.self="closeStagePicker">
          <div ref="stageDialog" class="dynamic-venue__stage-dialog" @keydown.esc.prevent="closeStagePicker" @keydown.tab="trapStagePickerFocus">
            <button class="dynamic-venue__stage-close" type="button" aria-label="Cerrar selector de entradas" @click="closeStagePicker">
              <i class="fas fa-times"></i>
            </button>

            <div class="dynamic-venue__stage-dialog-head">
              <small>{{ stagePickerEyebrow }}</small>
              <h3>{{ stagePickerSection.name }}</h3>
              <p>{{ stagePickerHelperText }}</p>
            </div>

            <div class="dynamic-venue__stage-choice-list" role="listbox" :aria-label="`Entradas disponibles en ${stagePickerSection.name}`">
              <button
                v-for="ticket in stagePickerTickets"
                :key="`stage-modal-${stagePickerSection.id}-${ticket.id}`"
                type="button"
                role="option"
                :aria-selected="Number(stagePickerTicket?.id) === Number(ticket.id)"
                :class="{ 'is-active': Number(stagePickerTicket?.id) === Number(ticket.id) }"
                @click="selectStagePickerTicket(ticket)"
              >
                <span>
                  <strong>{{ ticket.name }}</strong>
                  <em>{{ ticketAccessLabel(ticket) }}</em>
                </span>
                <b>{{ formatMoney(ticketGrossAmount(ticket)) }}</b>
              </button>
            </div>

            <button class="dynamic-venue__stage-confirm" type="button" :disabled="!stagePickerTicket || holding" @click="confirmStagePicker">
              <span>{{ holding ? 'Actualizando...' : stagePickerConfirmLabel }}</span>
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </transition>

      <aside class="dynamic-venue__summary" :class="{ 'is-empty': !hasSelection }">
        <div class="dynamic-venue__summary-head">
          <small>Resumen</small>
          <strong>{{ hasSelection ? `${selectionCount} acceso${selectionCount === 1 ? '' : 's'}` : 'Sin accesos' }}</strong>
          <p>{{ hasSelection ? 'Tus lugares están reservados temporalmente mientras avanzas.' : 'Elige una zona del mapa para comprar.' }}</p>
        </div>

        <div v-if="selectedSeats.length" class="dynamic-venue__selected-group">
          <small class="dynamic-venue__selected-group-label">Asientos y mesas</small>
          <div class="dynamic-venue__selected-list">
          <article v-for="item in selectedSeats" :key="item.seat.key" class="dynamic-venue__selected-item">
            <div class="dynamic-venue__selected-mark">
              <span>{{ item.seat.seat_name || item.seat.label }}</span>
            </div>
            <div class="dynamic-venue__selected-copy">
              <small>{{ item.ticket.name }}</small>
              <strong>{{ sectionNameForSeat(item.seat) || item.ticket.name }}</strong>
              <em>{{ ticketAccessLabel(item.ticket) }} · Reserva protegida hasta avanzar al pago</em>
            </div>
            <span>{{ seatSelectionPriceLabel(item) }}</span>
            <button type="button" @click="releaseSeat(item)"><i class="fas fa-trash"></i></button>
          </article>
          </div>
        </div>

        <div v-if="selectedZones.length" class="dynamic-venue__zone-cart">
          <small class="dynamic-venue__selected-group-label">Zonas por cantidad</small>
          <article v-for="item in selectedZones" :key="selectedZoneKey(item.section, item.ticket)" class="dynamic-venue__zone-cart-item">
            <div class="dynamic-venue__zone-cart-mark">
              <span>{{ item.quantity }}x</span>
            </div>
            <div class="dynamic-venue__zone-cart-copy">
              <small>{{ item.ticket.name }}</small>
              <strong>{{ item.section.name }}</strong>
              <em>{{ item.quantity }} unidad{{ item.quantity === 1 ? '' : 'es' }} visual{{ item.quantity === 1 ? '' : 'es' }} · {{ ticketAccessLabel(item.ticket) }}</em>
            </div>
            <span>{{ formatMoney(ticketGrossAmount(item.ticket) * item.quantity) }}</span>
            <button type="button" @click="removeZone(item.section, item.ticket)"><i class="fas fa-trash"></i></button>
          </article>
        </div>

        <div v-if="!hasSelection" class="dynamic-venue__empty-box">
          <i class="fas fa-map-location-dot"></i>
          <strong>Tu mapa está listo</strong>
          <span>Selecciona una zona o asiento y el resumen aparecerá aquí.</span>
        </div>

        <p v-if="holdError" class="dynamic-venue__hold-error">{{ holdError }}</p>

        <div class="dynamic-venue__totals">
          <div><span>Subtotal</span><strong>{{ formatMoney(subtotalBase) }}</strong></div>
          <div v-if="taxes > 0"><span>{{ taxSummaryLabel }}</span><strong>{{ formatMoney(taxes) }}</strong></div>
        </div>

        <button class="dynamic-venue__primary" type="button" :disabled="!canContinue || holding" @click="continueCheckout">
          <span>{{ continueButtonLabel }}</span>
          <i class="fas fa-arrow-right"></i>
        </button>
      </aside>
    </div>
  </section>
</template>

<script>
import { apiServiceSession } from '../../common/api.services.js'
import { ticketUnitPricing } from '../../common/ticketTaxes.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

export default {
  name: 'DynamicVenueMap',
  props: {
    event: { type: Object, required: true },
    code: { type: String, default: '' },
  },
  emits: ['messageFromDynamicVenue', 'fallback'],
  data() {
    return {
      loading: true,
      loadError: '',
      payload: null,
      layout: null,
      activeSectionId: '',
      sectionTicketSelections: {},
      selectedSeats: [],
      selectedZones: [],
      holdError: '',
      holding: false,
      customerSession: '',
      camera: { x: 0, y: -70, width: 1620, height: 1290 },
      animationFrame: null,
      cameraFrame: null,
      pendingCamera: null,
      svgRect: null,
      isPinching: false,
      pinchStartCamera: null,
      pinchStartDistance: 0,
      pinchStartMidpoint: null,
      pinchStartAnchor: null,
      pinchPreview: null,
      pinchPreviewTransform: '',
      hoveredSeatKey: '',
      isDragging: false,
      dragMoved: false,
      suppressNextClick: false,
      pointers: {},
      lastPointer: null,
      lastPinchDistance: 0,
      pendingSectionTapId: '',
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      stagePickerSectionId: '',
      stagePickerTicketId: '',
      stageChoiceConfirmations: {},
      pendingSeatForStagePicker: null,
      previousBodyOverflow: '',
    }
  },
  computed: {
    sections() {
      return (this.layout && Array.isArray(this.layout.sections) ? this.layout.sections : []).map((section) => ({
        ...section,
        shape: this.normalizeSectionShape(section.shape || this.fallbackSectionShape(section.id)),
      })).sort((first, second) => {
        const firstArea = (first.shape?.w || 0) * (first.shape?.h || 0)
        const secondArea = (second.shape?.w || 0) * (second.shape?.h || 0)
        return secondArea - firstArea
      })
    },
    visualSections() {
      return this.sections.filter((section) => !this.sectionIsVisuallyHidden(section))
    },
    objects() {
      return this.layout && Array.isArray(this.layout.objects) ? this.layout.objects : []
    },
    floorBox() {
      return this.layout?.view_box || { x: 0, y: -70, width: 1620, height: 1290 }
    },
    guideMetrics() {
      const box = this.floorBox
      const shortestSide = Math.min(box.width || 1, box.height || 1)
      return {
        cx: box.x + (box.width / 2),
        cy: box.y + (box.height / 2),
        innerRadius: Math.max(shortestSide * 0.24, 80),
        outerRadius: Math.max(shortestSide * 0.36, 130),
      }
    },
    seats() {
      return this.layout && Array.isArray(this.layout.seats) ? this.layout.seats : []
    },
    activeSection() {
      return this.sections.find((section) => section.id === this.activeSectionId) || null
    },
    isCompactViewport() {
      return this.viewportWidth <= 720
    },
    isSeatOnlyOverview() {
      const display = this.layout?.display || {}
      if (display.dense_seat_map === true || display.denseSeatMap === true || display.seat_only_overview === true || display.seatOnlyOverview === true) return true
      const configuredSections = this.sections.filter((section) => section.is_configured)
      return this.seats.length > 240 && configuredSections.length > 1 && configuredSections.every((section) => this.isSeatSection(section))
    },
    mapGuidance() {
      if (this.activeSection && this.isZoneSection(this.activeSection)) return 'Ajusta la cantidad para esta zona visual sin asiento numerado.'
      if (this.activeSection) return 'Mueve el mapa con un dedo, pellizca para acercarte y toca un asiento.'
      if (this.isSeatOnlyOverview) return 'Toca un asiento o elige una localidad arriba para enfocar esa zona.'
      return 'Toca una zona: algunas tienen asiento exacto y otras se compran por cantidad.'
    },
    visibleSeats() {
      let result = []
      if (!this.activeSection) {
        const seatSectionIds = this.sections.filter((section) => this.isSeatSection(section)).map((section) => section.id)
        result = this.seats.filter((seat) => seatSectionIds.includes(seat.section_id))
      } else {
        result = this.isSeatSection(this.activeSection)
          ? this.seats.filter((seat) => seat.section_id === this.activeSection.id || this.isSeatSelected(seat))
          : this.seats.filter((seat) => this.isSeatSelected(seat))
      }
      return result.slice().sort((left, right) => Number(this.isSeatSelected(left)) - Number(this.isSeatSelected(right)))
    },
    mapRenderItems() {
      const items = []
      this.visualSections.forEach((section, index) => {
        items.push({ type: 'section', key: `section:${section.id}`, element: section, zIndex: this.layerOrderValue(section, 'section', index), sourceIndex: index })
      })
      this.objects.forEach((object, index) => {
        items.push({ type: 'object', key: `object:${object.id}`, element: object, zIndex: this.layerOrderValue(object, 'object', index), sourceIndex: 10000 + index })
      })
      this.visibleSeats.forEach((seat, index) => {
        const selectedBoost = this.isSeatSelected(seat) ? 100000 : 0
        items.push({ type: 'seat', key: `seat:${seat.key}`, element: seat, zIndex: this.layerOrderValue(seat, 'seat', index) + selectedBoost, sourceIndex: 20000 + index })
      })
      return items.sort((left, right) => (left.zIndex - right.zIndex) || (left.sourceIndex - right.sourceIndex) || left.key.localeCompare(right.key))
    },
    hasSelection() {
      return Boolean(this.selectedSeats.length || this.selectedZones.length)
    },
    seatSelectionGroups() {
      const groups = []
      this.selectedSeats.forEach((item) => {
        const ticketId = item.ticket && item.ticket.id ? item.ticket.id : 'ticket'
        const isTable = this.isTableSeat(item.seat)
        const key = `${ticketId}-${isTable ? 'table' : 'seat'}`
        let group = groups.find((entry) => entry.key === key)
        if (!group) {
          group = {
            key,
            ticket: item.ticket,
            isTable,
            required: isTable ? 1 : this.ticketQuantityFactor(item.ticket),
            items: [],
          }
          groups.push(group)
        }
        group.items.push(item)
      })
      return groups
    },
    incompleteSeatGroup() {
      return this.seatSelectionGroups.find((group) => {
        const required = Math.max(Number.parseInt(group.required, 10) || 1, 1)
        return !group.isTable && required > 1 && group.items.length % required !== 0
      }) || null
    },
    incompleteSeatSelectionMessage() {
      const group = this.incompleteSeatGroup
      if (!group) return ''
      const missing = this.seatGroupMissingCount(group)
      return `Elige ${missing} asiento${missing === 1 ? '' : 's'} mas para ${group.ticket.name}.`
    },
    canContinue() {
      return Boolean(this.hasSelection && !this.incompleteSeatSelectionMessage)
    },
    continueButtonLabel() {
      if (this.holding) return 'Reservando...'
      if (!this.hasSelection) return 'Elige una zona'
      if (this.incompleteSeatSelectionMessage) return this.incompleteSeatSelectionMessage
      return `Continuar · ${this.formatMoney(this.subtotal)}`
    },
    mobileContinueLabel() {
      if (this.incompleteSeatSelectionMessage) return 'Completar combo'
      return 'Continuar'
    },
    selectionCount() {
      const seatCount = this.seatSelectionGroups.reduce((sum, group) => sum + this.seatGroupAccessCount(group), 0)
      const zoneCount = this.selectedZones.reduce((sum, item) => sum + this.selectedZoneAccessCount(item), 0)
      return seatCount + zoneCount
    },
    zonePickerSection() {
      return this.activeSection && this.isZoneSection(this.activeSection) && this.activeSection.ticket ? this.activeSection : null
    },
    activeTicketOptions() {
      return this.activeSection ? this.sectionTickets(this.activeSection) : []
    },
    stagePickerSection() {
      return this.sections.find((section) => section.id === this.stagePickerSectionId) || null
    },
    stagePickerTickets() {
      return this.stagePickerSection ? this.sectionTickets(this.stagePickerSection) : []
    },
    stagePickerTicket() {
      if (!this.stagePickerTickets.length) return null
      return this.stagePickerTickets.find((ticket) => Number(ticket.id) === Number(this.stagePickerTicketId)) || this.stagePickerTickets[0]
    },
    stagePickerSelectedSeats() {
      if (!this.stagePickerSection) return []
      return this.selectedSeats.filter((item) => item.seat.section_id === this.stagePickerSection.id)
    },
    stagePickerHasSelectedSeats() {
      return this.stagePickerSelectedSeats.length > 0
    },
    pendingStagePickerSeatLabel() {
      const seat = this.pendingSeatForStagePicker
      return seat ? (seat.seat_name || seat.label || seat.key) : ''
    },
    stagePickerEyebrow() {
      if (this.pendingSeatForStagePicker) return 'Elegir entrada'
      if (this.stagePickerSection && this.isZoneSection(this.stagePickerSection)) return 'Categoria de zona'
      return this.stagePickerHasSelectedSeats ? 'Entrada para proximos asientos' : 'Primero la entrada'
    },
    stagePickerSeatLabel() {
      if (!this.stagePickerSelectedSeats.length) return ''
      if (this.stagePickerSelectedSeats.length === 1) {
        const seat = this.stagePickerSelectedSeats[0].seat
        return seat.seat_name || seat.label || seat.key
      }
      return `${this.stagePickerSelectedSeats.length} asientos`
    },
    stagePickerHelperText() {
      if (this.stagePickerSection && this.isZoneSection(this.stagePickerSection)) {
        return 'Elige la categoria para esta zona. Luego ajustas cuantas unidades visuales quieres comprar.'
      }
      if (this.pendingSeatForStagePicker) {
        return `Elige con que entrada reservamos ${this.pendingStagePickerSeatLabel}. Si es combo, despues completa los asientos restantes.`
      }
      if (this.stagePickerHasSelectedSeats) {
        return `La entrada elegida se usara para los proximos asientos. Los asientos ya seleccionados mantienen su entrada actual.`
      }
      return 'Elige que entrada quieres comprar. Luego seleccionas los asientos exactos en el mapa.'
    },
    stagePickerConfirmLabel() {
      if (this.stagePickerSection && this.isZoneSection(this.stagePickerSection)) return 'Usar categoria'
      if (this.pendingSeatForStagePicker) return 'Elegir y reservar'
      return 'Usar esta entrada'
    },
    showSeatStageController() {
      return Boolean(this.activeSection && this.requiresStageChoice(this.activeSection) && !this.stagePickerSection)
    },
    viewBoxString() {
      const c = this.camera
      return `${c.x} ${c.y} ${c.width} ${c.height}`
    },
    subtotal() {
      const seatTotal = this.seatSelectionGroups.reduce((sum, group) => sum + (this.ticketGrossAmount(group.ticket) * this.seatGroupUnitQuantity(group)), 0)
      const zoneTotal = this.selectedZones.reduce((sum, item) => sum + (this.ticketGrossAmount(item.ticket) * item.quantity), 0)
      return seatTotal + zoneTotal
    },
    taxes() {
      const seatTaxes = this.seatSelectionGroups.reduce((sum, group) => sum + (this.ticketTaxAmount(group.ticket) * this.seatGroupUnitQuantity(group)), 0)
      const zoneTaxes = this.selectedZones.reduce((sum, item) => sum + (this.ticketTaxAmount(item.ticket) * item.quantity), 0)
      return seatTaxes + zoneTaxes
    },
    subtotalBase() {
      return Math.max(this.subtotal - this.taxes, 0)
    },
    taxSummaryLabel() {
      const labels = [...this.selectedSeats, ...this.selectedZones]
        .filter((item) => this.ticketTaxAmount(item.ticket) > 0)
        .map((item) => item.ticket.tax_label || 'IVA')
      const uniqueLabels = [...new Set(labels)]
      return uniqueLabels.length === 1 ? uniqueLabels[0] : 'Impuestos'
    },
    activePricingProfile() {
      const options = Array.isArray(this.event.payment_options) ? this.event.payment_options : []
      return options[0] && options[0].pricing_profile ? options[0].pricing_profile : null
    },
    fee() {
      if (!this.subtotal) return 0
      const profile = this.activePricingProfile
      const percent = Number.parseFloat(profile?.platform_fee_percent ?? this.event.coordinated_fee) || 0
      const perTicket = Number.parseFloat(profile?.platform_fee_per_ticket ?? this.event.coordinated_service) || 0
      const fixed = Number.parseFloat(profile?.platform_fee_fixed ?? this.event.fixed_fee) || 0
      return (this.subtotal * percent) + (perTicket * this.selectionCount) + fixed
    },
    total() {
      return this.subtotal + this.fee
    },
    selectedSeatSummary() {
      if (!this.selectedSeats.length) return 'Sin asientos'
      if (this.incompleteSeatSelectionMessage) return this.incompleteSeatSelectionMessage
      if (this.seatSelectionGroups.length === 1) {
        const group = this.seatSelectionGroups[0]
        const required = Math.max(Number.parseInt(group.required, 10) || 1, 1)
        if (group.isTable) {
          return `${group.items.length} mesa${group.items.length === 1 ? '' : 's'} · ${this.seatGroupAccessCount(group)} accesos`
        }
        if (required > 1) {
          const units = this.seatGroupUnitQuantity(group)
          return `${units} combo${units === 1 ? '' : 's'} · ${group.items.length} asientos`
        }
        if (group.items.length === 1) {
          const item = group.items[0]
          return `${item.seat.seat_name || item.seat.label} · 1 acceso`
        }
      }
      return `${this.selectionCount} accesos seleccionados`
    },
    selectedSelectionSummary() {
      if (this.selectedSeats.length) return this.selectedSeatSummary
      if (this.selectedZones.length === 1) {
        const item = this.selectedZones[0]
        return `${item.ticket.name} · ${this.selectedZoneAccessCount(item)} accesos`
      }
      return `${this.selectionCount} accesos seleccionados`
    },
    zonePickerSummary() {
      const section = this.zonePickerSection
      const ticket = this.selectedTicketForSection(section)
      if (!section || !ticket) return 'Elige una opcion disponible.'
      const quantity = this.zoneQuantity(section, ticket)
      const accessCount = quantity * this.ticketQuantityFactor(ticket)
      const unitLabel = quantity === 1 ? 'unidad' : 'unidades'
      const accessLabel = accessCount === 1 ? '1 acceso' : `${accessCount} accesos`
      return `${quantity} ${unitLabel} · ${accessLabel} · ${this.formatMoney(this.ticketGrossAmount(ticket) * quantity)}`
    },
    currency() {
      return normalizeCurrencyCode(this.event.presentment_currency || 'USD', 'USD')
    },
    previewSeatInfo() {
      const selected = this.selectedSeats[this.selectedSeats.length - 1]
      if (selected) {
        return {
          seat: selected.seat,
          ticket: selected.ticket,
          section: this.sectionForSeat(selected.seat),
          selected: true,
        }
      }

      const hovered = this.seats.find((seat) => seat.key === this.hoveredSeatKey)
      if (!hovered) return null
      return {
        seat: hovered,
        ticket: this.ticketForSeat(hovered),
        section: this.sectionForSeat(hovered),
        selected: false,
      }
    },
  },
  created() {
    this.customerSession = this.resolveCustomerSession()
    this.fetchVenueMap()
  },
  mounted() {
    this.syncViewportWidth()
    window.addEventListener('resize', this.syncViewportWidth, { passive: true })
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeUnmount() {
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
    if (this.cameraFrame) window.cancelAnimationFrame(this.cameraFrame)
    window.removeEventListener('resize', this.syncViewportWidth)
    window.removeEventListener('keydown', this.handleGlobalKeydown)
    this.unlockBodyScroll()
  },
  watch: {
    stagePickerSectionId(value) {
      if (value) {
        this.lockBodyScroll()
        this.$nextTick(this.focusStagePicker)
        return
      }
      this.unlockBodyScroll()
      this.$nextTick(() => {
        this.$el?.querySelector?.('.dynamic-venue__seat-stage-card button')?.focus?.()
      })
    },
  },
  methods: {
    layerOrderValue(element, type, index = 0) {
      const value = Number(element?.z_index ?? element?.zIndex)
      if (Number.isFinite(value)) return value
      if (type === 'object') return 2000 + index
      if (type === 'seat') return 3000 + index
      return 1000 + index
    },

    depthStyle(element, type = 'object') {
      const fallbackElevation = type === 'seat' ? 3 : (type === 'object' ? 2 : 1)
      const elevation = Math.max(Math.min(Number(element?.elevation ?? fallbackElevation) || fallbackElevation, 5), 0)
      const shadow = String(element?.shadow || 'soft').toLowerCase()
      const opacity = Number(element?.opacity)
      const style = {}
      if (Number.isFinite(opacity)) style.opacity = Math.max(Math.min(opacity, 1), 0.08)
      const shouldSkipSeatShadow = type === 'seat'
        && this.isSeatOnlyOverview
        && !this.isSeatSelected(element)
        && (!this.activeSection || this.isDragging || this.isPinching)
      if (shouldSkipSeatShadow) return style
      if (shadow !== 'none' && elevation > 0) {
        const compactScale = this.isCompactViewport ? 0.68 : 1
        const x = this.isCompactViewport ? Math.max(Math.round(elevation * 0.7), 1) : 0
        const y = Math.round((4 + (elevation * 5)) * compactScale)
        const blur = Math.round((10 + (elevation * 9)) * compactScale)
        const alpha = shadow === 'strong' ? 0.38 : 0.18 + (elevation * 0.035)
        style.filter = `drop-shadow(${x}px ${y}px ${blur}px rgba(0, 0, 0, ${Math.min(alpha, 0.42)}))`
      }
      return style
    },

    syncViewportWidth() {
      this.viewportWidth = window.innerWidth || this.viewportWidth
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.stagePickerSection) {
        event.preventDefault()
        this.closeStagePicker()
      }
    },
    focusableStagePickerElements() {
      const dialog = this.$refs.stageDialog
      if (!dialog) return []
      return Array.from(dialog.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null)
    },
    focusStagePicker() {
      const dialog = this.$refs.stageDialog
      if (!dialog) return
      const activeOption = dialog.querySelector('.dynamic-venue__stage-choice-list button.is-active')
      const firstFocusable = activeOption || this.focusableStagePickerElements()[0]
      firstFocusable?.focus?.()
    },
    trapStagePickerFocus(event) {
      const focusable = this.focusableStagePickerElements()
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    lockBodyScroll() {
      if (typeof document === 'undefined' || !document.body) return
      if (document.body.style.overflow !== 'hidden') {
        this.previousBodyOverflow = document.body.style.overflow || ''
      }
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll() {
      if (typeof document === 'undefined' || !document.body) return
      document.body.style.overflow = this.previousBodyOverflow || ''
      this.previousBodyOverflow = ''
    },
    resolveCustomerSession() {
      const key = 'fomo_dynamic_venue_session'
      const existing = window.sessionStorage.getItem(key)
      if (existing) return existing
      const next = `venue-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
      window.sessionStorage.setItem(key, next)
      return next
    },
    eventLookup() {
      return this.event.slug || this.event.id
    },
    fetchVenueMap() {
      const slug = this.eventLookup()
      const codeQuery = this.code ? `?code=${encodeURIComponent(this.code)}` : ''
      this.loading = true
      this.loadError = ''
      apiServiceSession(`/api/events/${slug}/venue-map/${codeQuery}`, undefined, undefined, { timeoutMs: 20000 }).then((data) => {
        if (!data || data.err || data.enabled === false) {
          this.loadError = data && (data.err || data.reason) ? (data.err || data.reason) : 'El mapa dinámico no está disponible.'
          return
        }
        this.payload = data
        this.layout = data.layout
        const viewBox = this.layout.view_box || { x: 0, y: -70, width: 1620, height: 1290 }
        this.camera = { ...viewBox }
      }).finally(() => {
        this.loading = false
      })
    },
    fallbackSectionShape(sectionId) {
      const shapes = {
        general: { x: 30, y: -55, w: 1560, h: 1230, rx: 34 },
        'ring-side-regular': { x: 280, y: 155, w: 1060, h: 890, rx: 28 },
        'ring-side-vip': { x: 465, y: 330, w: 690, h: 500, rx: 24 },
      }
      return shapes[sectionId] || { x: 80, y: 80, w: 400, h: 240, rx: 24 }
    },
    shouldShowSectionLabel(section) {
      return !this.activeSection && section && section.show_label !== false && section.hide_label !== true
    },
    sectionCustomPoint(section, key) {
      const point = section && section[key]
      if (!point || typeof point !== 'object') return null
      const x = Number(point.x)
      const y = Number(point.y)
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null
    },
    sectionLabelX(section) {
      return this.sectionCustomPoint(section, 'label_position')?.x ?? this.sectionCenterX(section)
    },
    sectionLabelY(section) {
      return this.sectionCustomPoint(section, 'label_position')?.y ?? ((Number(section?.shape?.y) || 0) + 42)
    },
    sectionPriceX(section) {
      return this.sectionCustomPoint(section, 'price_position')?.x ?? this.sectionLabelX(section)
    },
    sectionPriceY(section) {
      return this.sectionCustomPoint(section, 'price_position')?.y ?? (this.sectionLabelY(section) + 28)
    },
    formatMoney(value) {
      return formatCurrencyAmount(value, this.currency)
    },
    ticketPricing(ticket) {
      return ticketUnitPricing(ticket)
    },
    ticketGrossAmount(ticket) {
      return this.ticketPricing(ticket).grossAmount
    },
    ticketTaxAmount(ticket) {
      return this.ticketPricing(ticket).taxAmount
    },
    ticketQuantityFactor(ticket) {
      const factor = Number.parseInt(ticket && ticket.quantity, 10)
      return Number.isFinite(factor) && factor > 0 ? factor : 1
    },
    ticketAccessLabel(ticket) {
      const factor = this.ticketQuantityFactor(ticket)
      return factor > 1 ? `${factor} accesos por unidad` : '1 acceso por unidad'
    },
    isTableSeat(seat) {
      return Boolean(seat && seat.is_table)
    },
    tableStyleVariant(seat) {
      const variant = String(seat?.variant || seat?.symbol_variant || seat?.table_variant || '').toLowerCase()
      return ['circle', 'round'].includes(variant) ? 'circle' : 'default'
    },
    sectionTickets(section) {
      if (!section) return []
      if (Array.isArray(section.tickets) && section.tickets.length) return section.tickets
      return section.ticket ? [section.ticket] : []
    },
    selectedTicketForSection(section) {
      const tickets = this.sectionTickets(section)
      if (!tickets.length) return null
      const selectedId = this.sectionTicketSelections[section.id]
      return tickets.find((ticket) => Number(ticket.id) === Number(selectedId)) || tickets[0]
    },
    selectSectionTicket(section, ticket, event = null) {
      if (!section || !ticket) return
      const previousTicket = this.selectedTicketForSection(section)
      this.sectionTicketSelections = {
        ...this.sectionTicketSelections,
        [section.id]: ticket.id,
      }

      if (this.isZoneSection(section) && previousTicket && Number(previousTicket.id) !== Number(ticket.id)) {
        const previousItem = this.selectedZones.find((entry) => entry.section.id === section.id && Number(entry.ticket.id) === Number(previousTicket.id))
        const nextItem = this.selectedZones.find((entry) => entry.section.id === section.id && Number(entry.ticket.id) === Number(ticket.id))
        if (previousItem && !nextItem) {
          const max = Number.parseInt(ticket.max_quantity, 10) || 10
          this.selectedZones = this.selectedZones
            .filter((entry) => entry !== previousItem)
            .concat({ section, ticket, quantity: Math.min(max, previousItem.quantity) })
        }
      }

      this.$nextTick(() => {
        event?.currentTarget?.scrollIntoView?.({ block: 'nearest', inline: 'center' })
      })
    },
    sectionPriceLabel(section) {
      const tickets = this.sectionTickets(section)
      if (!tickets.length) return 'No disponible'
      const prices = tickets.map((ticket) => this.ticketGrossAmount(ticket)).filter((value) => Number.isFinite(value))
      const minPrice = prices.length ? Math.min(...prices) : 0
      const priceLabel = tickets.length > 1 ? `Desde ${this.formatMoney(minPrice)}` : this.formatMoney(minPrice)
      return tickets.length > 1 ? `${priceLabel} · ${tickets.length} opciones` : priceLabel
    },
    selectedZoneKey(section, ticket) {
      return `${section?.id || 'zone'}-${ticket?.id || 'ticket'}`
    },
    normalizeSectionShape(shape) {
      const source = shape && typeof shape === 'object' ? shape : {}
      const type = ['ellipse', 'polygon'].includes(source.type) ? source.type : 'rect'
      const normalized = {
        ...source,
        type,
        x: Number(source.x) || 0,
        y: Number(source.y) || 0,
        w: Math.max(Number(source.w) || Number(source.width) || 260, 1),
        h: Math.max(Number(source.h) || Number(source.height) || 160, 1),
        rx: Math.max(Number(source.rx) || 28, 0),
      }
      if (type === 'polygon') {
        const points = Array.isArray(source.points) ? source.points : []
        normalized.points = points.length >= 3 ? points : this.defaultPolygonPoints(normalized)
      }
      return normalized
    },
    sectionShapeType(section) {
      return ['ellipse', 'polygon'].includes(section?.shape?.type) ? section.shape.type : 'rect'
    },
    sectionVisualAreas(section) {
      const rawAreas = Array.isArray(section?.areas) ? section.areas : []
      const areas = rawAreas
        .map((area, index) => {
          const shape = this.normalizeSectionArea(area?.shape || area)
          return {
            key: `${section.id || 'section'}-area-${area?.id || index}`,
            shape,
          }
        })
        .filter((area) => area.shape && area.shape.w > 0 && area.shape.h > 0)
      return areas.length ? areas : [{ key: `${section.id || 'section'}-shape`, shape: section.shape }]
    },
    normalizeSectionArea(area) {
      const source = area && typeof area === 'object' ? area : {}
      const path = String(source.d || source.path || '').trim()
      if (path) {
        return {
          ...source,
          type: 'path',
          d: path,
          fill_rule: String(source.fill_rule || source.fillRule || 'evenodd'),
          x: Number(source.x) || 0,
          y: Number(source.y) || 0,
          w: Math.max(Number(source.w) || Number(source.width) || 1, 1),
          h: Math.max(Number(source.h) || Number(source.height) || 1, 1),
        }
      }
      return this.normalizeSectionShape(source)
    },
    sectionAreaShapeType(area) {
      return ['path', 'ellipse', 'polygon'].includes(area?.shape?.type) ? area.shape.type : 'rect'
    },
    sectionAreaPath(area) {
      return String(area?.shape?.d || '')
    },
    sectionAreaFillRule(area) {
      return String(area?.shape?.fill_rule || area?.shape?.fillRule || 'evenodd')
    },
    sectionAreaCenterX(area) {
      return (Number(area?.shape?.x) || 0) + ((Number(area?.shape?.w) || 0) / 2)
    },
    sectionAreaCenterY(area) {
      return (Number(area?.shape?.y) || 0) + ((Number(area?.shape?.h) || 0) / 2)
    },
    sectionAreaPolygonPoints(area) {
      const points = Array.isArray(area?.shape?.points) ? area.shape.points : []
      return points.map((point) => `${point.x},${point.y}`).join(' ')
    },
    sectionIsVisuallyHidden(section) {
      return Boolean(section && (section.visual_mode === 'hidden' || section.hidden || section.is_hidden))
    },
    objectShapeType(object) {
      const explicitType = String(object?.shape_type || object?.shapeType || '').toLowerCase()
      if (['ellipse', 'line', 'arrow', 'text'].includes(explicitType)) return explicitType
      const type = String(object?.type || '').toLowerCase()
      if (['column', 'circle', 'figure-circle'].includes(type)) return 'ellipse'
      if (['line', 'separator', 'figure-line', 'figure-separator'].includes(type)) return 'line'
      if (['arrow', 'figure-arrow'].includes(type)) return 'arrow'
      if (['text', 'label', 'figure-text', 'figure-label'].includes(type)) return 'text'
      return 'rect'
    },
    objectSemanticType(object) {
      const type = String(object?.type || '').toLowerCase()
      const semanticTypes = ['stage', 'entrance', 'exit', 'bathroom', 'bar', 'dj', 'screen', 'box-office', 'security', 'stairs', 'emergency', 'column', 'production', 'sofa', 'booth']
      return semanticTypes.includes(type) ? type : ''
    },
    objectIsSemantic(object) {
      return Boolean(this.objectSemanticType(object))
    },
    objectStageDeckPoints(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      if (this.objectStageVariant(object) === 'runway') {
        return [
          [x + (w * 0.08), y + (h * 0.12)],
          [x + (w * 0.92), y + (h * 0.12)],
          [x + (w * 0.92), y + (h * 0.46)],
          [x + (w * 0.62), y + (h * 0.46)],
          [x + (w * 0.62), y + (h * 0.9)],
          [x + (w * 0.38), y + (h * 0.9)],
          [x + (w * 0.38), y + (h * 0.46)],
          [x + (w * 0.08), y + (h * 0.46)],
        ].map((point) => point.join(',')).join(' ')
      }
      if (this.objectStageVariant(object) === 'corner') {
        return [
          [x + (w * 0.08), y + (h * 0.12)],
          [x + (w * 0.9), y + (h * 0.12)],
          [x + (w * 0.9), y + (h * 0.9)],
          [x + (w * 0.55), y + (h * 0.62)],
          [x + (w * 0.08), y + (h * 0.9)],
        ].map((point) => point.join(',')).join(' ')
      }
      return [
        [x + (w * 0.12), y + (h * 0.78)],
        [x + (w * 0.24), y + (h * 0.24)],
        [x + (w * 0.76), y + (h * 0.24)],
        [x + (w * 0.88), y + (h * 0.78)],
      ].map((point) => point.join(',')).join(' ')
    },

    objectVariant(object) {
      return String(object?.variant || object?.symbol_variant || '').toLowerCase()
    },

    objectStageVariant(object) {
      const variant = this.objectVariant(object)
      return ['front', 'arena', 'runway', 'corner'].includes(variant) ? variant : 'front'
    },

    objectStageRunwayLinePath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.16)} ${y + (h * 0.32)} H ${x + (w * 0.84)} M ${x + (w * 0.5)} ${y + (h * 0.46)} V ${y + (h * 0.84)}`
    },

    objectStageCornerLinePath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.18)} ${y + (h * 0.26)} H ${x + (w * 0.72)} V ${y + (h * 0.78)} M ${x + (w * 0.2)} ${y + (h * 0.78)} L ${x + (w * 0.74)} ${y + (h * 0.24)}`
    },

    objectBathroomVariant(object) {
      const variant = this.objectVariant(object)
      return ['wc', 'women', 'men', 'accessible'].includes(variant) ? variant : 'wc'
    },

    objectBathroomText(object) {
      const variant = this.objectBathroomVariant(object)
      if (variant === 'women') return 'D'
      if (variant === 'men') return 'H'
      if (variant === 'accessible') return 'ACC'
      return 'WC'
    },

    objectBathroomWomenPath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.34)} ${y + (h * 0.38)} L ${x + (w * 0.2)} ${y + (h * 0.76)} H ${x + (w * 0.48)} Z M ${x + (w * 0.28)} ${y + (h * 0.76)} V ${y + (h * 0.88)} M ${x + (w * 0.4)} ${y + (h * 0.76)} V ${y + (h * 0.88)}`
    },

    objectBathroomMenPath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.34)} ${y + (h * 0.38)} V ${y + (h * 0.74)} M ${x + (w * 0.22)} ${y + (h * 0.5)} H ${x + (w * 0.46)} M ${x + (w * 0.34)} ${y + (h * 0.74)} L ${x + (w * 0.24)} ${y + (h * 0.9)} M ${x + (w * 0.34)} ${y + (h * 0.74)} L ${x + (w * 0.44)} ${y + (h * 0.9)}`
    },

    objectBathroomMixedPath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.28)} ${y + (h * 0.42)} V ${y + (h * 0.74)} M ${x + (w * 0.18)} ${y + (h * 0.52)} H ${x + (w * 0.36)} M ${x + (w * 0.42)} ${y + (h * 0.42)} L ${x + (w * 0.34)} ${y + (h * 0.76)} H ${x + (w * 0.5)} Z`
    },

    objectBathroomAccessiblePath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.38)} ${y + (h * 0.38)} V ${y + (h * 0.58)} H ${x + (w * 0.5)} M ${x + (w * 0.38)} ${y + (h * 0.58)} L ${x + (w * 0.54)} ${y + (h * 0.82)} M ${x + (w * 0.24)} ${y + (h * 0.67)} C ${x + (w * 0.24)} ${y + (h * 0.84)} ${x + (w * 0.46)} ${y + (h * 0.84)} ${x + (w * 0.46)} ${y + (h * 0.67)}`
    },

    objectPortalPath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.22)} ${y + (h * 0.82)} V ${y + (h * 0.46)} Q ${x + (w * 0.5)} ${y + (h * 0.12)} ${x + (w * 0.78)} ${y + (h * 0.46)} V ${y + (h * 0.82)}`
    },
    objectShieldPath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      return `M ${x + (w * 0.5)} ${y + (h * 0.14)} L ${x + (w * 0.78)} ${y + (h * 0.28)} V ${y + (h * 0.5)} C ${x + (w * 0.78)} ${y + (h * 0.68)} ${x + (w * 0.63)} ${y + (h * 0.82)} ${x + (w * 0.5)} ${y + (h * 0.9)} C ${x + (w * 0.37)} ${y + (h * 0.82)} ${x + (w * 0.22)} ${y + (h * 0.68)} ${x + (w * 0.22)} ${y + (h * 0.5)} V ${y + (h * 0.28)} Z`
    },
    objectStairsPath(object) {
      const x = Number(object?.x) || 0
      const y = Number(object?.y) || 0
      const w = Number(object?.w) || 1
      const h = Number(object?.h) || 1
      const steps = 4
      const startX = x + (w * 0.18)
      const startY = y + (h * 0.78)
      const stepW = (w * 0.64) / steps
      const stepH = (h * 0.5) / steps
      let path = `M ${startX} ${startY}`
      for (let index = 0; index < steps; index += 1) {
        path += ` H ${startX + (stepW * (index + 1))} V ${startY - (stepH * (index + 1))}`
      }
      return path
    },
    objectColor(object) {
      return object?.color || (object?.type === 'entrance' || object?.type === 'exit' ? '#73f5a5' : '#dce5fb')
    },
    objectFill(object) {
      const color = this.objectColor(object)
      const configuredAlpha = Number(object?.fill_alpha ?? object?.fillAlpha)
      const alpha = Number.isFinite(configuredAlpha) ? Math.max(Math.min(configuredAlpha, 1), 0) : 0.18
      if (color && color.startsWith('#') && color.length === 7) {
        const red = Number.parseInt(color.slice(1, 3), 16)
        const green = Number.parseInt(color.slice(3, 5), 16)
        const blue = Number.parseInt(color.slice(5, 7), 16)
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`
      }
      return `rgba(196, 206, 228, ${alpha})`
    },
    objectFontSize(object) {
      const size = Number(object?.font_size || object?.fontSize)
      return Number.isFinite(size) && size > 0 ? `${size}px` : null
    },
    objectLetterSpacing(object) {
      const spacing = object?.letter_spacing || object?.letterSpacing
      return spacing != null ? String(spacing) : null
    },
    objectFontWeight(object) {
      const weight = Number(object?.font_weight || object?.fontWeight)
      return Number.isFinite(weight) && weight > 0 ? String(weight) : null
    },
    objectTextStrokeWidth(object) {
      const width = Number(object?.text_stroke_width ?? object?.stroke_width ?? object?.strokeWidth)
      return Number.isFinite(width) && width >= 0 ? `${width}px` : null
    },
    objectTextStrokeColor(object) {
      const color = object?.text_stroke_color || object?.textStrokeColor || object?.stroke_color || object?.strokeColor
      return color != null ? String(color) : null
    },
    objectTextStyle(object) {
      return {
        fill: this.objectColor(object),
        fontSize: this.objectFontSize(object),
        letterSpacing: this.objectLetterSpacing(object),
        fontWeight: this.objectFontWeight(object),
        stroke: this.objectTextStrokeColor(object),
        strokeWidth: this.objectTextStrokeWidth(object),
      }
    },
    objectShouldShowLabel(object) {
      if (object?.show_label === false || object?.hide_label === true) return false
      const shapeType = this.objectShapeType(object)
      const type = String(object?.type || '').toLowerCase()
      return shapeType !== 'line' && shapeType !== 'arrow' && shapeType !== 'text' && type !== 'figure-separator'
    },
    sectionCenterX(section) {
      return (Number(section?.shape?.x) || 0) + ((Number(section?.shape?.w) || 0) / 2)
    },
    sectionCenterY(section) {
      return (Number(section?.shape?.y) || 0) + ((Number(section?.shape?.h) || 0) / 2)
    },
    defaultPolygonPoints(shape) {
      const x = Number(shape?.x) || 0
      const y = Number(shape?.y) || 0
      const w = Number(shape?.w) || 260
      const h = Number(shape?.h) || 160
      return [
        { x, y: y + (h * 0.18) },
        { x: x + (w * 0.5), y },
        { x: x + w, y: y + (h * 0.18) },
        { x: x + w, y: y + h },
        { x, y: y + h },
      ]
    },
    sectionPolygonPoints(section) {
      const shape = section?.shape || {}
      const points = Array.isArray(shape.points) && shape.points.length >= 3 ? shape.points : this.defaultPolygonPoints(shape)
      return points.map((point) => `${Number(point.x) || 0},${Number(point.y) || 0}`).join(' ')
    },
    selectedZoneAccessCount(item) {
      if (!item) return 0
      return (Number.parseInt(item.quantity, 10) || 0) * this.ticketQuantityFactor(item.ticket)
    },
    seatGroupUnitQuantity(group) {
      if (!group) return 0
      if (group.isTable) return group.items.length
      const required = Math.max(Number.parseInt(group.required, 10) || 1, 1)
      return required > 1 ? Math.ceil(group.items.length / required) : group.items.length
    },
    seatGroupAccessCount(group) {
      if (!group) return 0
      if (group.isTable) return group.items.length * this.ticketQuantityFactor(group.ticket)
      return group.items.length
    },
    seatGroupMissingCount(group) {
      if (!group || group.isTable) return 0
      const required = Math.max(Number.parseInt(group.required, 10) || 1, 1)
      const remainder = group.items.length % required
      return remainder === 0 ? 0 : required - remainder
    },
    selectedSeatItemsFor(section, ticket, includeTables = false) {
      if (!section || !ticket) return []
      return this.selectedSeats.filter((item) => {
        if (item.seat.section_id !== section.id) return false
        if (Number(item.ticket.id) !== Number(ticket.id)) return false
        return includeTables ? this.isTableSeat(item.seat) : !this.isTableSeat(item.seat)
      })
    },
    shouldAskTicketForNextSeat(section, seat, ticket) {
      if (!section || !seat || !ticket || !this.requiresStageChoice(section) || this.isTableSeat(seat)) return false
      const required = this.ticketQuantityFactor(ticket)
      if (required <= 1) return false
      const selectedForTicket = this.selectedSeatItemsFor(section, ticket, false)
      return selectedForTicket.length > 0 && selectedForTicket.length % required === 0
    },
    seatSelectionPriceLabel(item) {
      if (!item) return ''
      if (this.isTableSeat(item.seat) || this.ticketQuantityFactor(item.ticket) <= 1) return this.formatMoney(this.ticketGrossAmount(item.ticket))
      return 'Incluido en combo'
    },
    sectionZoneQuantity(section) {
      if (!section) return 0
      return this.selectedZones
        .filter((entry) => entry.section.id === section.id)
        .reduce((sum, entry) => sum + entry.quantity, 0)
    },
    ticketPayload(ticket) {
      const pricing = this.ticketPricing(ticket)
      return {
        amount: pricing.grossAmount,
        base_amount: pricing.baseAmount,
        tax_amount: pricing.taxAmount,
        tax_label: pricing.taxLabel,
        tax_percent: ticket.tax_percent,
        tax_enabled: Boolean(ticket.tax_enabled),
        tax_price_includes_tax: pricing.priceIncludesTax,
      }
    },
    baseViewBox() {
      return this.floorBox || { x: 0, y: -70, width: 1620, height: 1290 }
    },
    activeCamera() {
      return this.pendingCamera || this.camera
    },
    formatTransformNumber(value) {
      const number = Number(value) || 0
      return Number(number.toFixed(4))
    },
    cacheSvgRect() {
      const rect = this.$refs.venueSvg?.getBoundingClientRect()
      this.svgRect = rect && rect.width && rect.height ? {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      } : null
      return this.svgRect
    },
    flushPendingCamera() {
      if (this.cameraFrame) {
        window.cancelAnimationFrame(this.cameraFrame)
        this.cameraFrame = null
      }
      if (this.pendingCamera) {
        this.camera = this.pendingCamera
        this.pendingCamera = null
      }
    },
    scheduleCamera(camera, immediate = false) {
      const nextCamera = this.clampCamera(camera)
      if (immediate || typeof window === 'undefined') {
        this.flushPendingCamera()
        this.camera = nextCamera
        return
      }

      this.pendingCamera = nextCamera
      if (this.cameraFrame) return

      this.cameraFrame = window.requestAnimationFrame(() => {
        this.cameraFrame = null
        if (!this.pendingCamera) return
        this.camera = this.pendingCamera
        this.pendingCamera = null
      })
    },
    resetPinchPreview() {
      this.isPinching = false
      this.pinchStartCamera = null
      this.pinchStartDistance = 0
      this.pinchStartMidpoint = null
      this.pinchStartAnchor = null
      this.pinchPreview = null
      this.pinchPreviewTransform = ''
    },
    startPinchPreview(points) {
      if (!Array.isArray(points) || points.length < 2) return
      this.flushPendingCamera()
      const camera = { ...this.camera }
      const rect = this.svgRect || this.cacheSvgRect()
      const distance = this.pointerDistance(points)
      if (!rect || distance <= 0) return
      const midpoint = this.pointerMidpoint(points)
      const anchor = this.clientToViewBox(midpoint.x, midpoint.y, camera)
      if (!anchor) return

      this.isPinching = true
      this.pinchStartCamera = camera
      this.pinchStartDistance = distance
      this.pinchStartMidpoint = midpoint
      this.pinchStartAnchor = anchor
      this.pinchPreview = null
      this.pinchPreviewTransform = ''
      this.lastPinchDistance = distance
    },
    updatePinchPreview(points) {
      if (!Array.isArray(points) || points.length < 2) return
      if (!this.isPinching || !this.pinchStartCamera || !this.pinchStartDistance || !this.pinchStartMidpoint || !this.pinchStartAnchor) {
        this.startPinchPreview(points)
      }
      if (!this.isPinching || !this.pinchStartCamera || !this.pinchStartDistance || !this.pinchStartMidpoint || !this.pinchStartAnchor) return

      const rect = this.svgRect || this.cacheSvgRect()
      if (!rect) return

      const distance = this.pointerDistance(points)
      if (distance <= 0) return

      const camera = this.pinchStartCamera
      const base = this.baseViewBox()
      const minWidth = base.width * 0.2
      const maxWidth = base.width * 1.06
      const minScale = camera.width / maxWidth
      const maxScale = camera.width / minWidth
      const rawScale = distance / this.pinchStartDistance
      const scale = Math.max(minScale, Math.min(maxScale, rawScale))
      const midpoint = this.pointerMidpoint(points)
      const dx = (midpoint.x - this.pinchStartMidpoint.x) * (camera.width / rect.width)
      const dy = (midpoint.y - this.pinchStartMidpoint.y) * (camera.height / rect.height)
      const translateX = dx + (this.pinchStartAnchor.x * (1 - scale))
      const translateY = dy + (this.pinchStartAnchor.y * (1 - scale))

      this.pinchPreview = { scale, translateX, translateY }
      this.pinchPreviewTransform = `matrix(${this.formatTransformNumber(scale)} 0 0 ${this.formatTransformNumber(scale)} ${this.formatTransformNumber(translateX)} ${this.formatTransformNumber(translateY)})`
      this.dragMoved = true
    },
    commitPinchPreview() {
      if (!this.isPinching) return
      const camera = this.pinchStartCamera
      const preview = this.pinchPreview
      if (camera && preview && preview.scale > 0) {
        this.scheduleCamera({
          x: (camera.x - preview.translateX) / preview.scale,
          y: (camera.y - preview.translateY) / preview.scale,
          width: camera.width / preview.scale,
          height: camera.height / preview.scale,
        }, true)
      }
      this.resetPinchPreview()
    },
    sectionTone(section) {
      const sectionId = typeof section === 'string' ? section : section?.id
      if (section && typeof section === 'object' && section.color) return section.color
      const tones = {
        general: '#7e91b8',
        'ring-side-regular': '#8e6bc4',
        'ring-side-vip': '#dce5fb',
      }
      return tones[sectionId] || '#cad6df'
    },
    sectionFill(section) {
      const color = this.sectionTone(section)
      const configuredAlpha = Number(section?.fill_alpha ?? section?.fillAlpha)
      const alpha = Number.isFinite(configuredAlpha) ? Math.max(Math.min(configuredAlpha, 1), 0) : 0.18
      if (color && color.startsWith('#') && color.length === 7) {
        const red = Number.parseInt(color.slice(1, 3), 16)
        const green = Number.parseInt(color.slice(3, 5), 16)
        const blue = Number.parseInt(color.slice(5, 7), 16)
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`
      }
      const sectionId = typeof section === 'string' ? section : section?.id
      const fills = {
        general: 'rgba(126, 145, 184, 0.16)',
        'ring-side-regular': 'rgba(142, 107, 196, 0.18)',
        'ring-side-vip': 'rgba(220, 229, 251, 0.18)',
      }
      return fills[sectionId] || 'rgba(202, 214, 223, 0.16)'
    },
    paddedBox(box, paddingRatio = 0.16) {
      const width = Number.parseFloat(box?.width || box?.w) || 360
      const height = Number.parseFloat(box?.height || box?.h) || 260
      const padding = Math.max(Math.min(width, height) * paddingRatio, 60)
      return {
        x: (Number.parseFloat(box?.x) || 0) - padding,
        y: (Number.parseFloat(box?.y) || 0) - padding,
        width: width + (padding * 2),
        height: height + (padding * 2),
      }
    },
    sectionSeatBounds(section) {
      if (!section) return null
      const seats = this.seats.filter((seat) => String(seat.section_id) === String(section.id))
      if (!seats.length) return null
      const minX = Math.min(...seats.map((seat) => Number(seat.x) || 0))
      const minY = Math.min(...seats.map((seat) => Number(seat.y) || 0))
      const maxX = Math.max(...seats.map((seat) => (Number(seat.x) || 0) + (Number(seat.w) || 34)))
      const maxY = Math.max(...seats.map((seat) => (Number(seat.y) || 0) + (Number(seat.h) || 26)))
      return {
        x: minX,
        y: minY,
        width: Math.max(maxX - minX, 1),
        height: Math.max(maxY - minY, 1),
      }
    },
    sectionFocusBox(section) {
      const base = this.baseViewBox()
      const rawBox = this.isSeatSection(section) ? (this.sectionSeatBounds(section) || section?.view_box || section?.shape || this.fallbackSectionShape(section?.id)) : (section?.view_box || section?.shape || this.fallbackSectionShape(section?.id))
      const shapeBox = {
        x: Number.parseFloat(rawBox?.x) || base.x,
        y: Number.parseFloat(rawBox?.y) || base.y,
        width: Number.parseFloat(rawBox?.width || rawBox?.w) || base.width,
        height: Number.parseFloat(rawBox?.height || rawBox?.h) || base.height,
      }
      const sectionArea = shapeBox.width * shapeBox.height
      const baseArea = Math.max((base.width || 1) * (base.height || 1), 1)
      const isLargeZone = this.isZoneSection(section) && !section?.has_seats && sectionArea / baseArea > 0.38

      if (isLargeZone) {
        return base
      }

      const paddingRatio = this.isSeatSection(section) || section?.has_seats ? 0.18 : 0.14
      return this.paddedBox(shapeBox, paddingRatio)
    },
    clampCamera(camera) {
      const base = this.baseViewBox()
      const minWidth = base.width * 0.2
      const maxWidth = base.width * 1.06
      const nextWidth = Math.max(minWidth, Math.min(maxWidth, camera.width))
      const ratio = camera.height / camera.width || this.camera.height / this.camera.width || (base.height / base.width)
      const nextHeight = nextWidth * ratio
      const bleedX = base.width * 0.08
      const bleedY = base.height * 0.08
      let nextX = camera.x
      let nextY = camera.y
      const minX = base.x - bleedX
      const maxX = base.x + base.width - nextWidth + bleedX
      const minY = base.y - bleedY
      const maxY = base.y + base.height - nextHeight + bleedY

      if (minX > maxX) nextX = base.x + ((base.width - nextWidth) / 2)
      else nextX = Math.max(minX, Math.min(maxX, nextX))

      if (minY > maxY) nextY = base.y + ((base.height - nextHeight) / 2)
      else nextY = Math.max(minY, Math.min(maxY, nextY))

      return { x: nextX, y: nextY, width: nextWidth, height: nextHeight }
    },
    focusSection(section) {
      this.activeSectionId = section.id
      this.animateCamera(this.sectionFocusBox(section))
    },
    goOverview() {
      this.closeStagePicker()
      this.activeSectionId = ''
      this.animateCamera(this.baseViewBox())
    },
    animateCamera(target) {
      this.flushPendingCamera()
      const from = { ...this.camera }
      const to = {
        x: target.x,
        y: target.y,
        width: target.width || target.w || from.width,
        height: target.height || target.h || from.height,
      }
      const duration = this.isCompactViewport && this.isSeatOnlyOverview ? 260 : 520
      const start = performance.now()
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration)
        const eased = easeOutCubic(progress)
        this.camera = {
          x: from.x + ((to.x - from.x) * eased),
          y: from.y + ((to.y - from.y) * eased),
          width: from.width + ((to.width - from.width) * eased),
          height: from.height + ((to.height - from.height) * eased),
        }
        if (progress < 1) this.animationFrame = window.requestAnimationFrame(tick)
      }
      this.animationFrame = window.requestAnimationFrame(tick)
    },
    clientToViewBox(clientX, clientY, camera = this.activeCamera()) {
      const rect = this.svgRect || this.cacheSvgRect()
      if (!rect || rect.width === 0 || rect.height === 0) return null
      const xRatio = (clientX - rect.left) / rect.width
      const yRatio = (clientY - rect.top) / rect.height
      return {
        x: camera.x + (xRatio * camera.width),
        y: camera.y + (yRatio * camera.height),
      }
    },
    zoomCamera(factor, clientX, clientY) {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
      const camera = this.activeCamera()
      const anchor = this.clientToViewBox(clientX, clientY, camera) || {
        x: camera.x + (camera.width / 2),
        y: camera.y + (camera.height / 2),
      }
      const nextWidth = camera.width * factor
      const nextHeight = camera.height * factor
      const widthRatio = nextWidth / camera.width
      const heightRatio = nextHeight / camera.height
      this.scheduleCamera({
        x: anchor.x - ((anchor.x - camera.x) * widthRatio),
        y: anchor.y - ((anchor.y - camera.y) * heightRatio),
        width: nextWidth,
        height: nextHeight,
      }, !this.isDragging)
    },
    zoomIn() {
      const svg = this.$refs.venueSvg
      const rect = svg?.getBoundingClientRect()
      this.zoomCamera(0.78, rect ? rect.left + (rect.width / 2) : 0, rect ? rect.top + (rect.height / 2) : 0)
    },
    zoomOut() {
      const svg = this.$refs.venueSvg
      const rect = svg?.getBoundingClientRect()
      this.zoomCamera(1.22, rect ? rect.left + (rect.width / 2) : 0, rect ? rect.top + (rect.height / 2) : 0)
    },
    resetCamera() {
      this.goOverview()
    },
    handleWheelZoom(event) {
      this.zoomCamera(event.deltaY > 0 ? 1.12 : 0.88, event.clientX, event.clientY)
    },
    handleDoubleClickZoom(event) {
      this.zoomCamera(0.7, event.clientX, event.clientY)
    },
    pointerList() {
      return Object.values(this.pointers)
    },
    pointerDistance(points) {
      if (points.length < 2) return 0
      const dx = points[0].x - points[1].x
      const dy = points[0].y - points[1].y
      return Math.hypot(dx, dy)
    },
    pointerMidpoint(points) {
      return {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
      }
    },
    beginMapInteraction(event) {
      if (event.button !== undefined && event.button !== 0) return
      if (event.target?.closest?.('.dynamic-venue__seat')) return
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
      this.flushPendingCamera()
      this.cacheSvgRect()
      this.$refs.venueSvg?.setPointerCapture?.(event.pointerId)
      this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY }
      const points = this.pointerList()
      this.isDragging = true
      this.dragMoved = false
      if (points.length === 1) {
        this.lastPointer = { x: event.clientX, y: event.clientY }
      }
      if (points.length === 2) {
        this.startPinchPreview(points)
      }
    },
    beginSectionTap(section) {
      this.pendingSectionTapId = section?.id || ''
    },
    moveMapInteraction(event) {
      if (!this.pointers[event.pointerId]) return
      this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY }
      const points = this.pointerList()
      if (points.length >= 2) {
        this.updatePinchPreview(points)
        return
      }
      if (!this.lastPointer) return
      const rect = this.svgRect || this.cacheSvgRect()
      if (!rect) return
      const dx = event.clientX - this.lastPointer.x
      const dy = event.clientY - this.lastPointer.y
      if (Math.abs(dx) + Math.abs(dy) > 6) this.dragMoved = true
      this.lastPointer = { x: event.clientX, y: event.clientY }
      const camera = this.activeCamera()
      this.scheduleCamera({
        x: camera.x - (dx * (camera.width / rect.width)),
        y: camera.y - (dy * (camera.height / rect.height)),
        width: camera.width,
        height: camera.height,
      })
    },
    endMapInteraction(event) {
      const hadMultiplePointers = this.pointerList().length > 1
      if (this.pointers[event.pointerId]) {
        delete this.pointers[event.pointerId]
      }
      const points = this.pointerList()
      if (this.isPinching && points.length < 2) {
        this.commitPinchPreview()
      }
      this.isDragging = points.length > 0
      this.lastPointer = points[0] || null
      this.lastPinchDistance = points.length >= 2 ? this.pointerDistance(points) : 0
      if (points.length >= 2 && !this.isPinching) {
        this.startPinchPreview(points)
      }
      if (!this.isDragging) {
        this.flushPendingCamera()
        this.svgRect = null
      }
      if (!this.isDragging && !this.dragMoved && !hadMultiplePointers && this.pendingSectionTapId) {
        const section = this.sections.find((entry) => entry.id === this.pendingSectionTapId)
        if (section) this.handleSectionClick(section)
      }
      if (!this.isDragging) {
        this.pendingSectionTapId = ''
      }
      if (!this.isDragging && this.dragMoved) {
        this.suppressNextClick = true
        window.setTimeout(() => { this.suppressNextClick = false }, 140)
      }
    },
    handleSectionClick(section) {
      if (this.suppressNextClick) return
      if (!section.is_configured) {
        this.holdError = 'Esta zona todavía no tiene entrada disponible.'
        return
      }
      this.focusSection(section)
      if (this.requiresStageChoice(section)) {
        this.openStagePicker(section)
        return
      }
      if (this.isZoneSection(section) && this.sectionTickets(section).length > 1 && this.sectionZoneQuantity(section) === 0) {
        this.openStagePicker(section)
        return
      }
      if (this.isZoneSection(section) && this.zoneQuantity(section) === 0) {
        this.incrementZone(section)
      }
    },
    isSeatSection(section) {
      return Boolean(section && (section.selection_mode || section.kind) === 'seat')
    },
    isZoneSection(section) {
      return Boolean(section && (section.selection_mode || section.kind) === 'zone')
    },
    requiresStageChoice(section) {
      return Boolean(section && this.isSeatSection(section) && this.sectionTickets(section).length > 1)
    },
    stageChoiceConfirmed(section) {
      if (!section || !this.requiresStageChoice(section)) return true
      const selectedTicket = this.selectedTicketForSection(section)
      return Boolean(selectedTicket && Number(this.stageChoiceConfirmations[section.id]) === Number(selectedTicket.id))
    },
    openStagePicker(section, pendingSeat = null) {
      if (!section || this.sectionTickets(section).length <= 1) return
      const selectedTicket = this.selectedTicketForSection(section)
      this.pendingSeatForStagePicker = pendingSeat ? { ...pendingSeat } : null
      this.stagePickerSectionId = section.id
      this.stagePickerTicketId = selectedTicket ? selectedTicket.id : this.sectionTickets(section)[0]?.id || ''
    },
    closeStagePicker() {
      this.stagePickerSectionId = ''
      this.stagePickerTicketId = ''
      this.pendingSeatForStagePicker = null
    },
    selectStagePickerTicket(ticket) {
      if (!ticket) return
      this.stagePickerTicketId = ticket.id
    },
    confirmStagePicker() {
      const section = this.stagePickerSection
      const ticket = this.stagePickerTicket
      if (!section || !ticket) return
      const pendingSeat = this.pendingSeatForStagePicker ? { ...this.pendingSeatForStagePicker } : null
      this.selectSectionTicket(section, ticket)
      this.stageChoiceConfirmations = {
        ...this.stageChoiceConfirmations,
        [section.id]: ticket.id,
      }
      this.closeStagePicker()
      if (this.isZoneSection(section) && this.zoneQuantity(section, ticket) === 0) {
        this.incrementZone(section)
        return
      }
      if (pendingSeat) {
        this.reserveSeat(pendingSeat, ticket)
      }
    },
    syncSelectedSeatsToTicket(section, ticket, previousTicket = null) {
      const seatsToUpdate = this.selectedSeats.filter((item) => item.seat.section_id === section.id)
      if (!seatsToUpdate.length) return Promise.resolve(true)

      this.holding = true
      this.holdError = ''
      return Promise.all(seatsToUpdate.map((item) => (
        apiServiceSession(`/api/events/${this.eventLookup()}/venue-map/holds/`, 'POST', {
          seat_key: item.seat.key,
          ticket_id: ticket.id,
          code: this.code || '',
          customer_session: this.customerSession,
        }, { timeoutMs: 15000 }).then((data) => {
          if (!data || data.err || !data.hold_token) {
            throw new Error(data && data.err ? data.err : 'No pudimos actualizar la entrada de ese asiento.')
          }
          return {
            key: item.seat.key,
            item: {
              ...item,
              ticket: data.ticket || ticket,
              hold: data,
            },
          }
        })
      ))).then((updates) => {
        const updatesBySeat = new Map(updates.map((entry) => [entry.key, entry.item]))
        this.selectedSeats = this.selectedSeats.map((item) => updatesBySeat.get(item.seat.key) || item)
        return true
      }).catch((error) => {
        this.holdError = error && error.message ? error.message : 'No pudimos actualizar la entrada de tus asientos.'
        if (previousTicket) {
          this.sectionTicketSelections = {
            ...this.sectionTicketSelections,
            [section.id]: previousTicket.id,
          }
          this.stageChoiceConfirmations = {
            ...this.stageChoiceConfirmations,
            [section.id]: previousTicket.id,
          }
        }
        return false
      }).finally(() => {
        this.holding = false
      })
    },
    zoneQuantity(section, ticket = null) {
      const selectedTicket = ticket || this.selectedTicketForSection(section)
      const item = this.selectedZones.find((entry) => entry.section.id === section.id && Number(entry.ticket.id) === Number(selectedTicket?.id))
      return item ? item.quantity : 0
    },
    incrementZone(section) {
      const ticket = this.selectedTicketForSection(section)
      if (!section || !ticket) return
      const current = this.selectedZones.find((entry) => entry.section.id === section.id && Number(entry.ticket.id) === Number(ticket.id))
      const max = Number.parseInt(ticket.max_quantity, 10) || 10
      if (current) {
        current.quantity = Math.min(max, current.quantity + 1)
        this.selectedZones = this.selectedZones.slice()
        return
      }
      this.selectedZones = this.selectedZones.concat({ section, ticket, quantity: 1 })
    },
    decrementZone(section) {
      const ticket = this.selectedTicketForSection(section)
      if (!section || !ticket) return
      const current = this.selectedZones.find((entry) => entry.section.id === section.id && Number(entry.ticket.id) === Number(ticket.id))
      if (!current) return
      if (current.quantity <= 1) {
        this.removeZone(section, ticket)
        return
      }
      current.quantity -= 1
      this.selectedZones = this.selectedZones.slice()
    },
    removeZone(section, ticket = null) {
      if (!section) return
      const selectedTicket = ticket || this.selectedTicketForSection(section)
      this.selectedZones = this.selectedZones.filter((entry) => entry.section.id !== section.id || Number(entry.ticket.id) !== Number(selectedTicket?.id))
    },
    sectionForSeat(seat) {
      return this.sections.find((section) => section.id === seat.section_id) || null
    },
    sectionNameForSeat(seat) {
      const section = this.sectionForSeat(seat)
      return section ? section.name : ''
    },
    ticketForSeat(seat) {
      const section = this.sectionForSeat(seat)
      return this.selectedTicketForSection(section)
    },
    seatTransform(seat) {
      const rotation = Number.parseFloat(seat.rotation) || 0
      return `translate(${seat.x} ${seat.y}) rotate(${rotation} ${seat.w / 2} ${seat.h / 2})`
    },
    isSeatSelected(seat) {
      return this.selectedSeats.some((item) => item.seat.key === seat.key)
    },
    seatClass(seat) {
      return {
        'is-available': seat.state === 'available',
        'is-occupied': seat.state === 'occupied' || seat.state === 'held' || seat.state === 'unconfigured',
        'is-selected': this.isSeatSelected(seat),
        'is-muted': this.activeSection && seat.section_id !== this.activeSection.id && !this.isSeatSelected(seat),
      }
    },
    shouldShowSeatLabel(seat) {
      return Boolean(this.activeSection && seat.section_id === this.activeSection.id) || this.isSeatSelected(seat)
    },
    hoverSeat(seat) {
      this.hoveredSeatKey = seat ? seat.key : ''
    },
    handleSeatClick(seat) {
      if (this.suppressNextClick) return
      let targetSection = this.activeSection
      if (!this.activeSection || this.activeSection.id !== seat.section_id) {
        const section = this.sectionForSeat(seat)
        if (section && section.is_configured) {
          this.focusSection(section)
          targetSection = section
        }
      }
      const selected = this.selectedSeats.find((item) => item.seat.key === seat.key)
      if (selected) {
        this.releaseSeat(selected)
        return
      }
      if (targetSection && this.requiresStageChoice(targetSection) && !this.stageChoiceConfirmed(targetSection)) {
        this.openStagePicker(targetSection, seat)
        return
      }
      const ticket = this.ticketForSeat(seat)
      if (this.shouldAskTicketForNextSeat(targetSection, seat, ticket)) {
        this.openStagePicker(targetSection, seat)
        return
      }
      this.reserveSeat(seat, ticket)
    },
    toggleSeat(seat, ticketOverride = null) {
      if (this.holding) return
      const selected = this.selectedSeats.find((item) => item.seat.key === seat.key)
      if (selected) {
        this.releaseSeat(selected)
        return
      }
      this.reserveSeat(seat, ticketOverride)
    },
    reserveSeat(seat, ticketOverride = null) {
      if (this.holding) return
      const ticket = ticketOverride || this.ticketForSeat(seat)
      if (!ticket || seat.state !== 'available') {
        this.holdError = 'Ese asiento no está disponible.'
        return
      }
      this.holding = true
      this.holdError = ''
      apiServiceSession(`/api/events/${this.eventLookup()}/venue-map/holds/`, 'POST', {
        seat_key: seat.key,
        ticket_id: ticket.id,
        code: this.code || '',
        customer_session: this.customerSession,
      }, { timeoutMs: 15000 }).then((data) => {
        if (!data || data.err || !data.hold_token) {
          this.holdError = data && data.err ? data.err : 'No pudimos reservar ese asiento.'
          return
        }
        this.selectedSeats = this.selectedSeats.concat({ seat: { ...seat, state: 'selected' }, ticket: data.ticket || ticket, hold: data })
      }).finally(() => {
        this.holding = false
      })
    },
    releaseSeat(item) {
      this.selectedSeats = this.selectedSeats.filter((selected) => selected.seat.key !== item.seat.key)
      if (item.hold && item.hold.hold_token) {
        apiServiceSession(`/api/events/${this.eventLookup()}/venue-map/holds/${item.hold.hold_token}/`, 'DELETE', undefined, { timeoutMs: 10000 })
      }
    },
    continueCheckout() {
      if (!this.hasSelection) return
      if (this.incompleteSeatSelectionMessage) {
        this.holdError = this.incompleteSeatSelectionMessage
        return
      }

      const products = this.selectedZones.map((item) => ({
        remaining_tickets: item.ticket.remaining_tickets,
        host_remaining_tickets: item.ticket.host_remaining_tickets,
        quantity: item.quantity,
        ...this.ticketPayload(item.ticket),
        name: item.ticket.name,
        is_courtesy: item.ticket.is_courtesy,
        total: this.ticketGrossAmount(item.ticket) * item.quantity,
        id: item.ticket.id,
        sales_date: item.ticket.sales_date,
        expiration_date: item.ticket.expiration_date,
        quantity_factor: this.ticketQuantityFactor(item.ticket),
        map: false,
        max_quantity: item.ticket.max_quantity,
        description: item.ticket.description,
        generic_image: item.ticket.generic_image,
        guest_fields: Array.isArray(item.ticket.guest_fields) ? item.ticket.guest_fields : [],
        dynamic_venue_section_id: item.section.id,
        dynamic_venue_section_name: item.section.name,
      }))

      const seatsProducts = []
      const selectedSeats = []
      this.seatSelectionGroups.forEach((group) => {
        const unitQuantity = this.seatGroupUnitQuantity(group)
        const factor = this.ticketQuantityFactor(group.ticket)
        seatsProducts.push({
          remaining_tickets: group.ticket.remaining_tickets,
          host_remaining_tickets: group.ticket.host_remaining_tickets,
          quantity: unitQuantity,
          ...this.ticketPayload(group.ticket),
          name: group.ticket.name,
          is_courtesy: group.ticket.is_courtesy,
          total: this.ticketGrossAmount(group.ticket) * unitQuantity,
          id: group.ticket.id,
          sales_date: group.ticket.sales_date,
          expiration_date: group.ticket.expiration_date,
          quantity_factor: factor,
          map: true,
          max_quantity: group.ticket.max_quantity,
          description: group.ticket.description,
          generic_image: group.ticket.generic_image,
          guest_fields: Array.isArray(group.ticket.guest_fields) ? group.ticket.guest_fields : [],
        })

        group.items.forEach((item) => {
          const seatPayload = {
            id: item.seat.key,
            name: item.seat.seat_name || item.seat.label,
            hold_token: item.hold?.hold_token || '',
          }
          if (group.isTable) {
            Array.from({ length: factor }).forEach(() => selectedSeats.push({ ...seatPayload }))
            return
          }
          selectedSeats.push(seatPayload)
        })
      })

      this.$emit('messageFromDynamicVenue', {
        products,
        seatsProducts,
        selectedSeats,
        subtotal: this.subtotal.toFixed(2),
        taxes: this.taxes.toFixed(2),
        fee: this.fee.toFixed(2),
        total: this.total.toFixed(2),
      })
    },
  },
}
</script>

<style scoped>
.dynamic-venue {
  --venue-accent: #dce5fb;
  --venue-accent-strong: #f4f7ff;
  --venue-violet: #8e6bc4;
  --venue-selection: #b19de4;
  --venue-selection-deep: #7054b8;
  --venue-selection-soft: #f8f5ff;
  --venue-blue: #95a8bb;
  --venue-ink: #05070d;
  --venue-panel: rgba(9, 13, 19, 0.86);
  --venue-stroke: rgba(196, 206, 228, 0.14);
  display: grid;
  gap: 18px;
  color: #f5f7fb;
}

.dynamic-venue__hero,
.dynamic-venue__map-card,
.dynamic-venue__summary,
.dynamic-venue__loading,
.dynamic-venue__error {
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    radial-gradient(circle at 10% 0%, rgba(220, 229, 251, 0.1), transparent 34%),
    radial-gradient(circle at 84% 12%, rgba(142, 107, 196, 0.18), transparent 32%),
    linear-gradient(145deg, rgba(13, 15, 20, 0.94), rgba(6, 8, 12, 0.98));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32);
  backdrop-filter: blur(24px) saturate(150%);
}

.dynamic-venue__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  border-radius: 28px;
}

.dynamic-venue__hero small,
.dynamic-venue__summary small {
  color: var(--venue-accent);
  font-weight: 900;
  letter-spacing: 0.16em;
}

.dynamic-venue__hero h2 {
  margin: 4px 0;
  font-size: clamp(1.55rem, 2.6vw, 2.7rem);
  font-weight: 950;
  letter-spacing: -0.04em;
}

.dynamic-venue__hero p,
.dynamic-venue__summary p {
  margin: 0;
  color: rgba(224, 233, 242, 0.74);
}

.dynamic-venue__hero-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.dynamic-venue__hero-pills span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border-radius: 999px;
  color: rgba(234, 241, 251, 0.86);
  font-size: 0.78rem;
  font-weight: 800;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025));
  border: 1px solid var(--venue-stroke);
}

.dynamic-venue__hero-pills i {
  color: var(--venue-accent);
}

.dynamic-venue__hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.dynamic-venue__timer,
.dynamic-venue__ghost,
.dynamic-venue__primary {
  border: 0;
  border-radius: 999px;
  font-weight: 900;
}

.dynamic-venue__timer {
  padding: 10px 14px;
  color: rgba(233, 239, 247, 0.76);
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__timer.is-active {
  color: #f4f7ff;
  background: rgba(142, 107, 196, 0.14);
  box-shadow: inset 0 0 0 1px rgba(220, 229, 251, 0.18);
}

.dynamic-venue__ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.dynamic-venue__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 410px);
  gap: 18px;
  align-items: start;
}

.dynamic-venue__map-card,
.dynamic-venue__summary {
  border-radius: 30px;
  overflow: hidden;
}

.dynamic-venue__map-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.035);
}

.dynamic-venue__zone-strip {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  overflow-x: auto;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018)),
    rgba(6, 9, 14, 0.72);
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
  scrollbar-width: none;
}

.dynamic-venue__zone-strip::-webkit-scrollbar {
  display: none;
}

.dynamic-venue__zone-strip button {
  flex: 0 0 auto;
  display: grid;
  gap: 2px;
  min-width: 146px;
  padding: 10px 13px;
  border: 1px solid rgba(196, 206, 228, 0.14);
  border-radius: 18px;
  color: rgba(234, 241, 251, 0.86);
  text-align: left;
  background:
    radial-gradient(circle at 18% 0%, rgba(220, 229, 251, 0.1), transparent 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.022));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.dynamic-venue__zone-strip button.is-active {
  color: #fff;
  border-color: rgba(220, 229, 251, 0.42);
  background:
    radial-gradient(circle at 12% 0%, rgba(244, 247, 255, 0.24), transparent 40%),
    radial-gradient(circle at 100% 100%, rgba(142, 107, 196, 0.22), transparent 42%),
    rgba(17, 24, 37, 0.88);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.14);
}

.dynamic-venue__zone-strip button:disabled {
  opacity: 0.46;
}

.dynamic-venue__zone-strip span {
  font-size: 0.78rem;
  font-weight: 950;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.dynamic-venue__zone-strip strong {
  color: var(--venue-accent);
  font-size: 0.9rem;
}

.dynamic-venue__breadcrumb button {
  border: 0;
  color: var(--venue-accent);
  font-weight: 900;
  background: transparent;
}

.dynamic-venue__breadcrumb {
  display: grid;
  gap: 3px;
}

.dynamic-venue__breadcrumb small {
  color: rgba(198, 207, 226, 0.76);
  font-size: 0.78rem;
  font-weight: 700;
}

.dynamic-venue__legend {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  color: rgba(229, 236, 247, 0.72);
  font-size: 0.78rem;
}

.dynamic-venue__legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.dynamic-venue__legend i {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.dynamic-venue__legend .is-available { background: #dfe6ea; }
.dynamic-venue__legend .is-selected { background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection)); }
.dynamic-venue__legend .is-occupied { background: #ff1717; }

.dynamic-venue__stage {
  position: relative;
  min-height: 620px;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 43%, rgba(244, 247, 255, 0.18), transparent 18%),
    radial-gradient(circle at 52% 58%, rgba(142, 107, 196, 0.24), transparent 44%),
    radial-gradient(circle at 18% 12%, rgba(149, 168, 187, 0.16), transparent 31%),
    linear-gradient(180deg, rgba(6, 9, 14, 0.95), rgba(4, 6, 10, 0.99));
}

.dynamic-venue__stage::before {
  content: '';
  position: absolute;
  inset: 14px;
  border-radius: 24px;
  pointer-events: none;
  border: 1px solid rgba(196, 206, 228, 0.1);
  box-shadow: inset 0 0 70px rgba(220, 229, 251, 0.08), inset 0 0 160px rgba(142, 107, 196, 0.08);
}

.dynamic-venue__stage::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(115deg, transparent 0%, rgba(244, 247, 255, 0.09) 42%, transparent 58%),
    radial-gradient(circle at 50% 50%, transparent 42%, rgba(0, 0, 0, 0.18) 100%);
  opacity: 0.58;
}

.dynamic-venue__svg {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  min-height: 620px;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.dynamic-venue__stage.is-dragging .dynamic-venue__svg {
  cursor: grabbing;
}

.dynamic-venue__stage.is-pinching .dynamic-venue__zone,
.dynamic-venue__stage.is-pinching .dynamic-venue__seat,
.dynamic-venue__stage.is-dragging .dynamic-venue__zone,
.dynamic-venue__stage.is-dragging .dynamic-venue__seat {
  transition: none;
}

.dynamic-venue__stage.is-pinching .dynamic-venue__zone rect,
.dynamic-venue__stage.is-pinching .dynamic-venue__zone ellipse,
.dynamic-venue__stage.is-pinching .dynamic-venue__zone polygon,
.dynamic-venue__stage.is-pinching .dynamic-venue__zone-aura,
.dynamic-venue__stage.is-pinching .dynamic-venue__object-ring-shadow,
.dynamic-venue__stage.is-pinching .dynamic-venue__object-ring-floor,
.dynamic-venue__stage.is-pinching .dynamic-venue__seat,
.dynamic-venue__stage.is-pinching .dynamic-venue__seat rect,
.dynamic-venue__stage.is-dragging .dynamic-venue__zone rect,
.dynamic-venue__stage.is-dragging .dynamic-venue__zone ellipse,
.dynamic-venue__stage.is-dragging .dynamic-venue__zone polygon,
.dynamic-venue__stage.is-dragging .dynamic-venue__zone-aura,
.dynamic-venue__stage.is-dragging .dynamic-venue__object-ring-shadow,
.dynamic-venue__stage.is-dragging .dynamic-venue__object-ring-floor,
.dynamic-venue__stage.is-dragging .dynamic-venue__seat,
.dynamic-venue__stage.is-dragging .dynamic-venue__seat rect {
  filter: none !important;
}

.dynamic-venue__stage.is-pinching .dynamic-venue__seat-halo,
.dynamic-venue__stage.is-dragging .dynamic-venue__seat-halo {
  animation: none !important;
}

.dynamic-venue__floor {
  fill: url(#dynamicVenueFloorDepth);
  stroke: rgba(224, 232, 248, 0.16);
  stroke-width: 2;
  filter: drop-shadow(0 30px 68px rgba(0, 0, 0, 0.34));
}

.dynamic-venue__floor-grid {
  fill: url(#dynamicVenueGrid);
  opacity: 0.38;
  pointer-events: none;
}

.dynamic-venue__floor-sheen,
.dynamic-venue__floor-vignette {
  pointer-events: none;
}

.dynamic-venue__floor-sheen {
  fill: url(#dynamicVenueFloorSheen);
  opacity: 0.58;
}

.dynamic-venue__floor-vignette {
  fill: url(#dynamicVenueFloorVignette);
  opacity: 0.76;
}

.dynamic-venue__guides {
  pointer-events: none;
}

.dynamic-venue__guides line,
.dynamic-venue__guides circle {
  fill: none;
  stroke: rgba(226, 234, 248, 0.075);
  stroke-width: 1.6;
  stroke-dasharray: 16 18;
}

.dynamic-venue__zone {
  cursor: pointer;
  transition: opacity 180ms ease, filter 180ms ease;
}

.dynamic-venue__zone rect:not(.dynamic-venue__zone-aura),
.dynamic-venue__zone ellipse:not(.dynamic-venue__zone-aura),
.dynamic-venue__zone polygon:not(.dynamic-venue__zone-aura) {
  opacity: 0.86;
  stroke-width: 2;
  filter: drop-shadow(0 18px 38px rgba(0, 0, 0, 0.24));
  stroke-linejoin: round;
}

.dynamic-venue__zone-aura {
  fill: none;
  opacity: 0.18;
  stroke-width: 4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone-sheen {
  fill: url(#dynamicVenueZoneSheen);
  opacity: 0.22;
  pointer-events: none;
}

.dynamic-venue__zone text {
  fill: #fff;
  font-size: 28px;
  font-weight: 950;
  letter-spacing: 0.08em;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.55);
  stroke-width: 5px;
}

.dynamic-venue__zone .dynamic-venue__zone-price {
  font-size: 20px;
  fill: #dce5fb;
}

.dynamic-venue__zone.is-muted {
  opacity: 0.28;
}

.dynamic-venue__zone.is-active rect,
.dynamic-venue__zone.is-active ellipse,
.dynamic-venue__zone.is-active polygon {
  opacity: 0.88;
  stroke-width: 4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone.has-zone-selection rect:not(.dynamic-venue__zone-aura),
.dynamic-venue__zone.has-zone-selection ellipse:not(.dynamic-venue__zone-aura),
.dynamic-venue__zone.has-zone-selection polygon:not(.dynamic-venue__zone-aura) {
  opacity: 0.92;
  stroke: var(--venue-selection-soft);
  stroke-width: 4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone.has-zone-selection .dynamic-venue__zone-aura {
  opacity: 0.42;
}

.dynamic-venue__zone.is-active .dynamic-venue__zone-sheen,
.dynamic-venue__zone.has-zone-selection .dynamic-venue__zone-sheen,
.dynamic-venue__zone.is-preview .dynamic-venue__zone-sheen {
  opacity: 0.34;
}

.dynamic-venue__zone.is-preview rect,
.dynamic-venue__zone.is-preview ellipse,
.dynamic-venue__zone.is-preview polygon {
  opacity: 0.9;
  stroke-width: 3.4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone.is-unconfigured rect,
.dynamic-venue__zone.is-unconfigured ellipse,
.dynamic-venue__zone.is-unconfigured polygon {
  opacity: 0.12;
}

.dynamic-venue__object-ring-shadow {
  fill: rgba(4, 6, 10, 0.52);
  filter: blur(12px);
}

.dynamic-venue__object-ring-floor {
  fill: url(#dynamicVenueRing);
  stroke: rgba(255, 255, 255, 0.5);
  stroke-width: 4;
  filter: drop-shadow(0 24px 52px rgba(124, 112, 214, 0.34));
}

.dynamic-venue__object-ring-inner {
  fill: rgba(255, 255, 255, 0.12);
  stroke: rgba(5, 8, 14, 0.24);
  stroke-width: 2;
}

.dynamic-venue__object-ring line {
  stroke: rgba(255, 255, 255, 0.54);
  stroke-width: 4;
  stroke-linecap: round;
}

.dynamic-venue__object-shape {
  stroke-width: 3;
}

.dynamic-venue__object-line {
  fill: none;
  stroke-width: 8;
  stroke-linecap: round;
  opacity: 0.9;
  filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.28));
}

.dynamic-venue__object {
  pointer-events: none;
}

.dynamic-venue__object text {
  fill: #061116;
  font-size: 32px;
  font-weight: 950;
  letter-spacing: 0.16em;
}

.dynamic-venue__semantic-object {
  color: var(--object-color, #dce5fb);
}

.dynamic-venue__semantic-fill {
  fill: var(--object-color, #dce5fb);
  stroke: rgba(5, 8, 14, 0.48);
  stroke-width: 2.5;
  filter: drop-shadow(0 16px 24px rgba(0, 0, 0, 0.28));
}

.dynamic-venue__semantic-stroke,
.dynamic-venue__semantic-line {
  fill: none;
  stroke: var(--object-color, #dce5fb);
  stroke-width: 6;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 14px 22px rgba(0, 0, 0, 0.25));
}

.dynamic-venue__semantic-line.is-thin {
  stroke-width: 3.2;
}

.dynamic-venue__semantic-stroke-fill {
  fill: var(--object-fill, rgba(220, 229, 251, 0.16));
  stroke: var(--object-color, #dce5fb);
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 16px 26px rgba(0, 0, 0, 0.25));
}

.dynamic-venue__semantic-text {
  fill: var(--object-color, #dce5fb) !important;
  font-size: 30px !important;
  font-weight: 760 !important;
  letter-spacing: 0.05em !important;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.5);
  stroke-width: 4px;
}

.dynamic-venue__semantic-label {
  fill: rgba(238, 244, 255, 0.9) !important;
  font-size: 20px !important;
  font-weight: 720 !important;
  letter-spacing: 0.12em !important;
  text-transform: uppercase;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.62);
  stroke-width: 5px;
}

.dynamic-venue__object-text-only {
  font-size: 30px;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.58);
  stroke-width: 6px;
}

.dynamic-venue__seat {
  cursor: pointer;
  transition: opacity 160ms ease, transform 160ms ease, filter 160ms ease;
  filter: drop-shadow(0 7px 8px rgba(0, 0, 0, 0.28));
}

.dynamic-venue__seat rect {
  fill: url(#dynamicVenueSeatAvailable);
  stroke: rgba(4, 7, 12, 0.58);
  stroke-width: 1.2;
}

.dynamic-venue__seat.is-table {
  filter: drop-shadow(0 14px 18px rgba(0, 0, 0, 0.42));
}

.dynamic-venue__table-aura {
  fill: rgba(255, 219, 137, 0.1);
  stroke: rgba(248, 245, 255, 0.16);
  stroke-width: 1.4;
}

.dynamic-venue__table-chair {
  fill: rgba(235, 242, 255, 0.82);
  stroke: rgba(10, 12, 18, 0.6);
  stroke-width: 1.1;
}

.dynamic-venue__table-top {
  fill: #f7d985;
  stroke: rgba(255, 247, 213, 0.94);
  stroke-width: 2;
  filter: drop-shadow(0 0 12px rgba(214, 177, 95, 0.28));
}

.dynamic-venue__table-round {
  fill: rgba(8, 10, 16, 0.78);
  stroke: rgba(244, 247, 255, 0.94);
  stroke-width: 3;
}

.dynamic-venue__table-round-label {
  fill: #f4f7ff !important;
  font-size: 28px !important;
  font-weight: 900;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.7);
  stroke-width: 4px;
}

.dynamic-venue__seat .dynamic-venue__seat-hit {
  fill: transparent;
  stroke: none;
  pointer-events: all;
}

.dynamic-venue__seat:hover {
  filter: drop-shadow(0 0 12px rgba(220, 229, 251, 0.4));
}

.dynamic-venue:not(.is-section-focused) .dynamic-venue__seat:not(.is-selected) {
  opacity: 0.62;
  filter: drop-shadow(0 5px 7px rgba(0, 0, 0, 0.2));
}

.dynamic-venue:not(.is-section-focused) .dynamic-venue__seat.is-occupied {
  opacity: 0.72;
}

.dynamic-venue__seat text {
  fill: #111827;
  font-size: 11px;
  font-weight: 900;
  pointer-events: none;
}

.dynamic-venue__seat .dynamic-venue__seat-halo {
  fill: rgba(177, 157, 228, 0.2);
  stroke: rgba(248, 245, 255, 0.96);
  stroke-width: 2.8;
  filter: url(#dynamicVenueGlow);
  animation: dynamicVenueSeatPulse 1.35s ease-in-out infinite;
}

.dynamic-venue__seat.is-selected rect:not(.dynamic-venue__seat-halo):not(.dynamic-venue__seat-hit) {
  fill: url(#dynamicVenueSeatSelected);
  stroke: var(--venue-selection-soft);
  stroke-width: 3;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__seat.is-table.is-selected .dynamic-venue__table-chair {
  fill: #fff7d6;
}

.dynamic-venue__seat.is-occupied {
  cursor: not-allowed;
}

.dynamic-venue__seat.is-occupied rect:not(.dynamic-venue__seat-hit) {
  fill: #dc2f45;
  opacity: 0.72;
}

.dynamic-venue__seat.is-muted {
  opacity: 0.18;
}

.dynamic-venue__map-hint {
  position: absolute;
  left: 16px;
  bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  color: #fff;
  background: rgba(5, 8, 13, 0.68);
  border: 1px solid rgba(196, 206, 228, 0.14);
  backdrop-filter: blur(14px);
}

.dynamic-venue__map-hint i {
  color: var(--venue-accent);
}

.dynamic-venue__zoom-controls {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
  display: grid;
  gap: 8px;
}

.dynamic-venue__zoom-controls button {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(196, 206, 228, 0.16);
  border-radius: 15px;
  color: #eef3fa;
  background: rgba(7, 10, 16, 0.72);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.26), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px) saturate(150%);
}

.dynamic-venue__zoom-controls button:hover {
  color: var(--venue-accent-strong);
  border-color: rgba(220, 229, 251, 0.38);
}

.dynamic-venue__seat-peek {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 2;
  display: grid;
  gap: 3px;
  min-width: 190px;
  padding: 14px 16px;
  border-radius: 22px;
  color: #f4f8fb;
  background:
    radial-gradient(circle at 20% 0%, rgba(220, 229, 251, 0.16), transparent 42%),
    linear-gradient(145deg, rgba(10, 14, 21, 0.88), rgba(5, 7, 12, 0.94));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: 0 22px 44px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px) saturate(150%);
}

.dynamic-venue__seat-peek small {
  color: var(--venue-accent);
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dynamic-venue__seat-peek strong {
  font-size: 2.25rem;
  line-height: 1;
  letter-spacing: -0.06em;
}

.dynamic-venue__seat-peek span {
  color: rgba(214, 224, 248, 0.82);
  font-weight: 800;
}

.dynamic-venue__ticket-switcher {
  position: absolute;
  top: 18px;
  left: 50%;
  z-index: 4;
  display: grid;
  gap: 10px;
  width: min(430px, calc(100% - 148px));
  padding: 13px;
  border-radius: 26px;
  color: #f8fbff;
  background:
    radial-gradient(circle at 0% 0%, rgba(248, 245, 255, 0.2), transparent 35%),
    radial-gradient(circle at 100% 0%, rgba(112, 84, 184, 0.24), transparent 40%),
    linear-gradient(145deg, rgba(7, 10, 16, 0.88), rgba(4, 6, 10, 0.96));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: 0 22px 54px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transform: translateX(-50%);
  backdrop-filter: blur(24px) saturate(150%);
}

.dynamic-venue__ticket-switcher small {
  color: var(--venue-accent);
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.dynamic-venue__ticket-options {
  display: grid;
  gap: 8px;
}

.dynamic-venue__ticket-options button {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 18px;
  color: #eef4ff;
  text-align: left;
  border: 1px solid rgba(220, 229, 251, 0.12);
  background: rgba(255, 255, 255, 0.055);
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;
}

.dynamic-venue__ticket-options button:hover,
.dynamic-venue__ticket-options button.is-active {
  border-color: rgba(248, 245, 255, 0.46);
  background: linear-gradient(135deg, rgba(248, 245, 255, 0.16), rgba(112, 84, 184, 0.25));
  transform: translateY(-1px);
}

.dynamic-venue__ticket-options span {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.dynamic-venue__ticket-options strong {
  overflow: hidden;
  font-size: 0.92rem;
  letter-spacing: -0.03em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dynamic-venue__ticket-options em {
  color: rgba(214, 224, 248, 0.72);
  font-size: 0.72rem;
  font-style: normal;
  font-weight: 800;
}

.dynamic-venue__ticket-options b {
  color: #fff;
  font-size: 0.86rem;
  font-weight: 950;
}

.dynamic-venue__seat-stage-card {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 4;
  display: grid;
  gap: 5px;
  width: min(390px, calc(100% - 96px));
  padding: 14px;
  border-radius: 24px;
  color: #f8fbff;
  background:
    radial-gradient(circle at 0% 0%, rgba(248, 245, 255, 0.18), transparent 36%),
    radial-gradient(circle at 100% 100%, rgba(112, 84, 184, 0.2), transparent 42%),
    linear-gradient(145deg, rgba(7, 10, 16, 0.84), rgba(4, 6, 10, 0.94));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: 0 22px 54px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(22px) saturate(150%);
}

.dynamic-venue__seat-stage-card small {
  color: var(--venue-selection-soft);
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.dynamic-venue__seat-stage-card strong {
  overflow: hidden;
  font-size: 1rem;
  letter-spacing: -0.03em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dynamic-venue__seat-stage-card span {
  color: rgba(214, 224, 248, 0.76);
  font-size: 0.78rem;
  font-weight: 800;
}

.dynamic-venue__seat-stage-card button {
  justify-self: start;
  margin-top: 5px;
  padding: 8px 12px;
  border: 1px solid rgba(248, 245, 255, 0.24);
  border-radius: 999px;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 950;
  background: linear-gradient(135deg, rgba(248, 245, 255, 0.16), rgba(112, 84, 184, 0.34));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.dynamic-venue__stage-modal {
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: grid;
  place-items: center;
  padding: max(18px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(18px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left));
  background:
    radial-gradient(circle at 50% 28%, rgba(177, 157, 228, 0.16), transparent 34%),
    rgba(1, 3, 8, 0.68);
  backdrop-filter: blur(7px) saturate(120%);
}

.dynamic-venue__stage-dialog {
  position: relative;
  display: grid;
  gap: 16px;
  width: min(520px, 100%);
  max-height: min(720px, calc(100svh - 36px));
  overflow: auto;
  padding: 22px;
  border-radius: 32px;
  color: #f8fbff;
  background:
    radial-gradient(circle at 12% 0%, rgba(248, 245, 255, 0.18), transparent 34%),
    radial-gradient(circle at 100% 20%, rgba(112, 84, 184, 0.28), transparent 40%),
    linear-gradient(145deg, rgba(12, 15, 24, 0.97), rgba(4, 6, 11, 0.99));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.58), 0 0 44px rgba(142, 107, 196, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.dynamic-venue__stage-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(220, 229, 251, 0.14);
  border-radius: 14px;
  color: #eef3ff;
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__stage-dialog-head {
  display: grid;
  gap: 6px;
  padding-right: 42px;
}

.dynamic-venue__stage-dialog-head small {
  color: var(--venue-selection-soft);
  font-size: 0.74rem;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.dynamic-venue__stage-dialog-head h3 {
  margin: 0;
  font-size: clamp(1.65rem, 4vw, 2.35rem);
  font-weight: 950;
  letter-spacing: -0.06em;
}

.dynamic-venue__stage-dialog-head p {
  margin: 0;
  color: rgba(214, 224, 248, 0.78);
  font-weight: 750;
  line-height: 1.35;
}

.dynamic-venue__stage-choice-list {
  display: grid;
  gap: 10px;
}

.dynamic-venue__stage-choice-list button {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 15px;
  border: 1px solid rgba(220, 229, 251, 0.12);
  border-radius: 22px;
  color: #eef4ff;
  text-align: left;
  background: rgba(255, 255, 255, 0.055);
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;
}

.dynamic-venue__stage-choice-list button:hover,
.dynamic-venue__stage-choice-list button.is-active {
  border-color: rgba(248, 245, 255, 0.52);
  background:
    radial-gradient(circle at 12% 0%, rgba(248, 245, 255, 0.16), transparent 34%),
    linear-gradient(135deg, rgba(248, 245, 255, 0.14), rgba(112, 84, 184, 0.28));
  transform: translateY(-1px);
}

.dynamic-venue__stage-choice-list span {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.dynamic-venue__stage-choice-list strong {
  overflow: hidden;
  font-size: 1rem;
  letter-spacing: -0.03em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dynamic-venue__stage-choice-list em {
  color: rgba(214, 224, 248, 0.74);
  font-size: 0.78rem;
  font-style: normal;
  font-weight: 850;
}

.dynamic-venue__stage-choice-list b {
  padding: 7px 10px;
  border-radius: 999px;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 950;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 0 0 1px rgba(220, 229, 251, 0.12);
}

.dynamic-venue__stage-confirm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  color: #100d1a;
  font-weight: 950;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 58%, var(--venue-selection-deep));
  box-shadow: 0 18px 34px rgba(112, 84, 184, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.dynamic-venue__stage-confirm:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.dynamic-venue-stage-picker-enter-active,
.dynamic-venue-stage-picker-leave-active {
  transition: opacity 160ms ease;
}

.dynamic-venue-stage-picker-enter-active .dynamic-venue__stage-dialog,
.dynamic-venue-stage-picker-leave-active .dynamic-venue__stage-dialog {
  transition: transform 180ms ease, opacity 180ms ease;
}

.dynamic-venue-stage-picker-enter-from,
.dynamic-venue-stage-picker-leave-to {
  opacity: 0;
}

.dynamic-venue-stage-picker-enter-from .dynamic-venue__stage-dialog,
.dynamic-venue-stage-picker-leave-to .dynamic-venue__stage-dialog {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

.dynamic-venue__zone-picker {
  position: absolute;
  left: 18px;
  bottom: 18px;
  z-index: 3;
  display: grid;
  gap: 7px;
  width: min(470px, calc(100% - 92px));
  min-width: 230px;
  padding: 15px;
  border-radius: 24px;
  color: #f5f7ff;
  background:
    radial-gradient(circle at 18% 0%, rgba(220, 229, 251, 0.14), transparent 36%),
    radial-gradient(circle at 100% 100%, rgba(112, 84, 184, 0.2), transparent 42%),
    linear-gradient(145deg, rgba(8, 11, 17, 0.9), rgba(4, 6, 10, 0.96));
  border: 1px solid rgba(196, 206, 228, 0.16);
  box-shadow: 0 22px 52px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(22px) saturate(150%);
}

.dynamic-venue__zone-picker small {
  color: var(--venue-selection-soft);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dynamic-venue__zone-picker-title strong {
  font-size: 1.12rem;
  letter-spacing: -0.03em;
}

.dynamic-venue__zone-picker-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.dynamic-venue__zone-picker-title span {
  display: inline-grid;
  place-items: center;
  min-width: 44px;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  color: #140f24;
  font-weight: 950;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 62%, var(--venue-selection-deep));
  box-shadow: 0 12px 24px rgba(112, 84, 184, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.dynamic-venue__zone-ticket-button {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 10px;
  width: 100%;
  padding: 11px 12px;
  border: 1px solid rgba(220, 229, 251, 0.14);
  border-radius: 18px;
  color: #eef4ff;
  text-align: left;
  background:
    radial-gradient(circle at 12% 0%, rgba(248, 245, 255, 0.12), transparent 34%),
    rgba(255, 255, 255, 0.055);
}

.dynamic-venue__zone-ticket-button span {
  color: rgba(214, 224, 248, 0.7);
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dynamic-venue__zone-ticket-button strong {
  overflow: hidden;
  font-size: 0.94rem;
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dynamic-venue__zone-ticket-button i {
  grid-column: 2;
  grid-row: 1 / span 2;
  color: var(--venue-selection-soft);
}

.dynamic-venue__zone-options {
  display: grid;
  gap: 8px;
}

.dynamic-venue__zone-options button {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 11px;
  border: 1px solid rgba(220, 229, 251, 0.12);
  border-radius: 17px;
  color: #eef4ff;
  text-align: left;
  background: rgba(255, 255, 255, 0.055);
}

.dynamic-venue__zone-options button.is-active {
  border-color: rgba(248, 245, 255, 0.5);
  background:
    radial-gradient(circle at 14% 0%, rgba(248, 245, 255, 0.16), transparent 36%),
    linear-gradient(135deg, rgba(248, 245, 255, 0.14), rgba(112, 84, 184, 0.28));
}

.dynamic-venue__zone-options span {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.dynamic-venue__zone-options strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dynamic-venue__zone-options em {
  color: rgba(214, 224, 248, 0.72);
  font-size: 0.74rem;
  font-style: normal;
  font-weight: 800;
}

.dynamic-venue__zone-options b {
  color: #fff;
  font-size: 0.84rem;
  font-weight: 950;
}

.dynamic-venue__zone-picker > span {
  color: rgba(214, 224, 248, 0.78);
  font-weight: 800;
}

.dynamic-venue__zone-stepper {
  display: grid;
  grid-template-columns: 42px 1fr 42px;
  align-items: center;
  gap: 8px;
}

.dynamic-venue__zone-stepper button {
  width: 42px;
  height: 42px;
  border: 1px solid rgba(220, 229, 251, 0.16);
  border-radius: 15px;
  color: #eef3ff;
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__zone-stepper b {
  display: grid;
  place-items: center;
  min-height: 42px;
  border-radius: 15px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(220, 229, 251, 0.12);
}

.dynamic-venue__zone-continue {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  border: 0;
  border-radius: 999px;
  color: #120e20;
  font-weight: 950;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 58%, var(--venue-selection-deep));
  box-shadow: 0 16px 30px rgba(112, 84, 184, 0.26), inset 0 1px 0 rgba(255, 255, 255, 0.38);
}

.dynamic-venue__zone-continue:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.dynamic-venue__summary {
  position: sticky;
  top: 16px;
  display: grid;
  gap: 14px;
  padding: 18px;
}

.dynamic-venue__summary-head {
  display: grid;
  gap: 6px;
}

.dynamic-venue__summary-head strong {
  font-size: 1.35rem;
}

.dynamic-venue__selected-group,
.dynamic-venue__zone-cart {
  display: grid;
  gap: 8px;
}

.dynamic-venue__selected-group-label {
  color: rgba(214, 224, 248, 0.66);
  font-size: 0.7rem;
  font-weight: 950;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.dynamic-venue__selected-list {
  display: grid;
  gap: 10px;
  max-height: 280px;
  overflow: auto;
}

.dynamic-venue__selected-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 13px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 10% 0%, rgba(220, 229, 251, 0.14), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.dynamic-venue__selected-copy {
  display: grid;
  gap: 3px;
}

.dynamic-venue__selected-copy em {
  color: rgba(198, 207, 226, 0.72);
  font-size: 0.74rem;
  font-style: normal;
  font-weight: 700;
}

.dynamic-venue__selected-mark {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  color: #140f24;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 56%, var(--venue-selection-deep));
  box-shadow: 0 16px 28px rgba(142, 107, 196, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.dynamic-venue__selected-mark span {
  font-weight: 950;
  letter-spacing: -0.08em;
}

.dynamic-venue__selected-item button {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: rgba(220, 47, 69, 0.76);
}

.dynamic-venue__zone-cart-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 13px;
  border: 1px solid rgba(177, 157, 228, 0.2);
  border-radius: 22px;
  background:
    radial-gradient(circle at 8% 0%, rgba(177, 157, 228, 0.18), transparent 34%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(112, 84, 184, 0.08));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.dynamic-venue__zone-cart-mark {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  color: #140f24;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 56%, var(--venue-selection-deep));
  box-shadow: 0 16px 28px rgba(142, 107, 196, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.dynamic-venue__zone-cart-mark span {
  font-weight: 950;
  letter-spacing: -0.06em;
}

.dynamic-venue__zone-cart-copy {
  display: grid;
  gap: 3px;
}

.dynamic-venue__zone-cart-copy small {
  color: rgba(214, 224, 248, 0.72);
  font-weight: 850;
}

.dynamic-venue__zone-cart-copy em {
  color: rgba(198, 207, 226, 0.72);
  font-size: 0.74rem;
  font-style: normal;
  font-weight: 700;
}

.dynamic-venue__zone-cart-item > span {
  font-weight: 950;
  white-space: nowrap;
}

.dynamic-venue__zone-cart-item button {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: rgba(220, 47, 69, 0.76);
}

.dynamic-venue__empty-box,
.dynamic-venue__loading,
.dynamic-venue__error {
  display: grid;
  place-items: center;
  gap: 8px;
  min-height: 220px;
  padding: 24px;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.04);
}

.dynamic-venue__empty-box i {
  font-size: 2rem;
  color: var(--venue-accent);
}

.dynamic-venue__totals {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__totals div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.dynamic-venue__totals span {
  color: rgba(225, 232, 242, 0.7);
}

.dynamic-venue__totals .is-total {
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1.1rem;
}

.dynamic-venue__primary {
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 15px 18px;
  color: #eef3fa;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.12), transparent 36%),
    linear-gradient(180deg, rgba(24, 39, 48, 0.34), rgba(11, 11, 17, 0.72));
  border: 1px solid rgba(196, 206, 228, 0.16);
  box-shadow: 0 18px 34px rgba(2, 4, 10, 0.3), 0 10px 18px rgba(126, 145, 184, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.dynamic-venue__primary::before {
  content: '';
  position: absolute;
  inset: -12px;
  pointer-events: none;
  background:
    radial-gradient(circle at 16% 10%, rgba(228, 232, 239, 0.14), transparent 28%),
    radial-gradient(circle at 82% 84%, rgba(142, 107, 196, 0.18), transparent 34%);
  filter: blur(16px);
  opacity: 0.22;
}

.dynamic-venue__mobile-cta {
  display: none;
}

.dynamic-venue__primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dynamic-venue__hold-error {
  margin: 0;
  padding: 10px 12px;
  border-radius: 14px;
  color: #ffd8d2;
  background: rgba(255, 40, 40, 0.12);
}

.dynamic-venue__hold-toast {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: calc(100% - 36px);
  margin: 0;
  padding: 11px 14px;
  border-radius: 18px;
  color: #ffe4df;
  background: rgba(49, 10, 16, 0.82);
  border: 1px solid rgba(255, 116, 116, 0.24);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px) saturate(150%);
}

.dynamic-venue__loader-ring {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.12);
  border-top-color: var(--venue-accent);
  animation: dynamicVenueSpin 860ms linear infinite;
}

@keyframes dynamicVenueSpin {
  to { transform: rotate(360deg); }
}

@keyframes dynamicVenueSeatPulse {
  0%, 100% { opacity: 0.58; stroke-width: 2.3; }
  50% { opacity: 1; stroke-width: 3.8; }
}

@media (max-width: 1024px) {
  .dynamic-venue__grid {
    grid-template-columns: 1fr;
  }

  .dynamic-venue__summary {
    position: sticky;
    bottom: 12px;
    top: auto;
    z-index: 4;
  }
}

@media (max-width: 720px) {
  .dynamic-venue {
    gap: 8px;
    margin-inline: -10px;
  }

  .dynamic-venue.has-selection {
    padding-bottom: 92px;
  }

  .dynamic-venue.has-zone-picker {
    padding-bottom: 238px;
  }

  .dynamic-venue.has-zone-picker.has-selection {
    padding-bottom: 256px;
  }

  .dynamic-venue__map-card {
    border-radius: 26px;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.46);
  }

  .dynamic-venue__map-toolbar {
    align-items: flex-start;
    flex-direction: column;
    padding: 12px 13px 10px;
    gap: 6px;
  }

  .dynamic-venue__breadcrumb {
    width: 100%;
  }

  .dynamic-venue__breadcrumb button {
    padding: 0;
    font-size: 0.82rem;
  }

  .dynamic-venue__breadcrumb span {
    color: #fff;
    font-weight: 900;
  }

  .dynamic-venue__breadcrumb small {
    max-width: 320px;
    font-size: 0.74rem;
    line-height: 1.25;
  }

  .dynamic-venue__zone-strip {
    gap: 8px;
    padding: 10px 12px;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
  }

  .dynamic-venue__zone-strip button {
    min-width: 132px;
    padding: 9px 11px;
    border-radius: 16px;
    scroll-snap-align: start;
  }

  .dynamic-venue__zone-strip span {
    font-size: 0.7rem;
  }

  .dynamic-venue__zone-strip strong {
    font-size: 0.82rem;
  }

  .dynamic-venue__stage,
  .dynamic-venue__svg {
    min-height: clamp(440px, 64svh, 610px);
    height: clamp(440px, 64svh, 610px);
  }

  .dynamic-venue__stage {
    background:
      radial-gradient(circle at 50% 32%, rgba(244, 247, 255, 0.2), transparent 20%),
      radial-gradient(circle at 64% 60%, rgba(142, 107, 196, 0.24), transparent 48%),
      linear-gradient(180deg, rgba(8, 12, 18, 0.98), rgba(2, 4, 8, 1));
  }

  .dynamic-venue__stage::before {
    inset: 10px;
    box-shadow: inset 0 0 54px rgba(220, 229, 251, 0.08), inset 0 -58px 120px rgba(0, 0, 0, 0.28);
  }

  .dynamic-venue__stage::after {
    background:
      linear-gradient(122deg, rgba(255, 255, 255, 0.11), transparent 32%, transparent 70%, rgba(255, 255, 255, 0.045)),
      radial-gradient(circle at 50% 50%, transparent 36%, rgba(0, 0, 0, 0.24) 100%);
    opacity: 0.54;
  }

  .dynamic-venue__floor {
    stroke-width: 2.5;
    filter: drop-shadow(0 18px 38px rgba(0, 0, 0, 0.42));
  }

  .dynamic-venue__floor-grid {
    opacity: 0.24;
  }

  .dynamic-venue__floor-sheen {
    opacity: 0.72;
  }

  .dynamic-venue__floor-vignette {
    opacity: 0.9;
  }

  .dynamic-venue__zone rect:not(.dynamic-venue__zone-aura):not(.dynamic-venue__zone-sheen),
  .dynamic-venue__zone ellipse:not(.dynamic-venue__zone-aura):not(.dynamic-venue__zone-sheen),
  .dynamic-venue__zone polygon:not(.dynamic-venue__zone-aura):not(.dynamic-venue__zone-sheen) {
    stroke-width: 2.4;
    filter: none;
  }

  .dynamic-venue__zone-sheen {
    opacity: 0.32;
  }

  .dynamic-venue__semantic-fill,
  .dynamic-venue__semantic-stroke,
  .dynamic-venue__semantic-line,
  .dynamic-venue__semantic-stroke-fill,
  .dynamic-venue__object-line {
    filter: drop-shadow(1px 7px 12px rgba(0, 0, 0, 0.28));
  }

  .dynamic-venue__seat {
    filter: drop-shadow(1px 4px 5px rgba(0, 0, 0, 0.3)) !important;
  }

  .dynamic-venue:not(.is-section-focused) .dynamic-venue__seat:not(.is-selected) {
    opacity: 0.56;
    filter: drop-shadow(1px 3px 4px rgba(0, 0, 0, 0.22)) !important;
  }

  .dynamic-venue__seat.is-table {
    filter: drop-shadow(1px 8px 12px rgba(0, 0, 0, 0.36)) !important;
  }

  .dynamic-venue__seat.is-selected {
    filter: drop-shadow(0 0 12px rgba(220, 229, 251, 0.42)) !important;
  }

  .dynamic-venue__legend {
    display: none;
  }

  .dynamic-venue__seat-peek {
    display: none;
  }

  .dynamic-venue__ticket-switcher {
    top: 12px;
    left: 12px;
    right: 58px;
    width: auto;
    padding: 10px;
    border-radius: 20px;
    transform: none;
  }

  .dynamic-venue__ticket-options {
    grid-auto-flow: column;
    grid-auto-columns: minmax(268px, 82vw);
    overflow-x: auto;
    padding-bottom: 2px;
    scroll-snap-type: x mandatory;
  }

  .dynamic-venue__ticket-options button {
    grid-template-columns: 1fr;
    align-content: space-between;
    min-height: 96px;
    gap: 8px;
    padding: 13px;
    scroll-snap-align: start;
  }

  .dynamic-venue__ticket-options strong {
    display: -webkit-box;
    overflow: hidden;
    white-space: normal;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .dynamic-venue__ticket-options em {
    line-height: 1.18;
  }

  .dynamic-venue__ticket-options b {
    justify-self: start;
    padding: 5px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: inset 0 0 0 1px rgba(220, 229, 251, 0.1);
  }

  .dynamic-venue__seat-stage-card {
    top: 12px;
    left: 12px;
    right: 58px;
    width: auto;
    gap: 4px;
    padding: 11px;
    border-radius: 20px;
  }

  .dynamic-venue__seat-stage-card strong {
    font-size: 0.9rem;
  }

  .dynamic-venue__seat-stage-card span {
    font-size: 0.72rem;
    line-height: 1.2;
  }

  .dynamic-venue__seat-stage-card button {
    margin-top: 3px;
    padding: 7px 10px;
    font-size: 0.72rem;
  }

  .dynamic-venue__stage-modal {
    align-items: center;
    padding: 16px 12px;
  }

  .dynamic-venue__stage-dialog {
    gap: 14px;
    width: 100%;
    max-height: calc(100svh - 32px);
    padding: 18px;
    border-radius: 28px;
  }

  .dynamic-venue__stage-dialog-head {
    padding-right: 36px;
  }

  .dynamic-venue__stage-dialog-head h3 {
    font-size: 1.55rem;
  }

  .dynamic-venue__stage-dialog-head p {
    font-size: 0.9rem;
  }

  .dynamic-venue__stage-choice-list button {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 14px;
  }

  .dynamic-venue__stage-choice-list b {
    justify-self: start;
  }

  .dynamic-venue__stage-confirm {
    min-height: 50px;
  }

  .dynamic-venue__zone-picker {
    position: fixed;
    left: max(12px, env(safe-area-inset-left));
    right: max(12px, env(safe-area-inset-right));
    bottom: max(14px, calc(env(safe-area-inset-bottom) + 14px));
    z-index: 1250;
    gap: 10px;
    width: auto;
    min-width: 0;
    padding: 16px;
    border-radius: 28px;
    box-shadow: 0 26px 66px rgba(0, 0, 0, 0.56), 0 0 36px rgba(142, 107, 196, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  .dynamic-venue.has-selection .dynamic-venue__zone-picker {
    bottom: max(14px, calc(env(safe-area-inset-bottom) + 14px));
  }

  .dynamic-venue__zone-picker small {
    font-size: 0.72rem;
  }

  .dynamic-venue__zone-picker-title strong {
    font-size: 1.22rem;
  }

  .dynamic-venue__zone-options {
    grid-auto-flow: column;
    grid-auto-columns: minmax(244px, 82vw);
    overflow-x: auto;
    padding-bottom: 2px;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }

  .dynamic-venue__zone-options::-webkit-scrollbar {
    display: none;
  }

  .dynamic-venue__zone-options button {
    grid-template-columns: 1fr;
    align-content: space-between;
    min-height: 88px;
    scroll-snap-align: start;
  }

  .dynamic-venue__zone-options strong {
    white-space: normal;
  }

  .dynamic-venue__zone-options b {
    justify-self: start;
    padding: 5px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: inset 0 0 0 1px rgba(220, 229, 251, 0.1);
  }

  .dynamic-venue__zone-stepper {
    grid-template-columns: 48px 1fr 48px;
  }

  .dynamic-venue__zone-stepper button,
  .dynamic-venue__zone-stepper b {
    height: 48px;
    border-radius: 17px;
  }

  .dynamic-venue__zoom-controls {
    top: 12px;
    right: 12px;
  }

  .dynamic-venue__zoom-controls button {
    width: 38px;
    height: 38px;
    border-radius: 14px;
  }

  .dynamic-venue__selected-item {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .dynamic-venue__selected-item > span {
    grid-column: 2 / 4;
  }

  .dynamic-venue__map-hint {
    left: 12px;
    right: 12px;
    bottom: 14px;
    align-items: flex-start;
    border-radius: 20px;
    font-size: 0.78rem;
    line-height: 1.25;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.34);
  }

  .dynamic-venue__hold-toast {
    left: 12px;
    right: 12px;
    bottom: 78px;
    max-width: none;
    width: auto;
  }

  .dynamic-venue__mobile-cta {
    position: fixed;
    left: max(12px, env(safe-area-inset-left));
    right: max(12px, env(safe-area-inset-right));
    bottom: max(10px, env(safe-area-inset-bottom));
    z-index: 1200;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    padding: 10px 10px 10px 14px;
    border-radius: 24px;
    background:
      radial-gradient(circle at 14% 0%, rgba(221, 229, 247, 0.12), transparent 38%),
      radial-gradient(circle at 100% 100%, rgba(142, 107, 196, 0.18), transparent 44%),
      linear-gradient(145deg, rgba(9, 13, 19, 0.94), rgba(4, 6, 10, 0.98));
    border: 1px solid rgba(196, 206, 228, 0.16);
    box-shadow: 0 22px 54px rgba(0, 0, 0, 0.48), 0 0 28px rgba(142, 107, 196, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(22px) saturate(150%);
  }

  .dynamic-venue__mobile-cta small,
  .dynamic-venue__mobile-cta strong {
    display: block;
  }

  .dynamic-venue__mobile-cta small {
    color: rgba(198, 207, 226, 0.78);
    font-size: 0.76rem;
    font-weight: 800;
  }

  .dynamic-venue__mobile-cta .dynamic-venue__mobile-tax {
    margin-top: 2px;
    color: rgba(222, 230, 244, 0.72);
    font-size: 0.68rem;
    font-weight: 700;
  }

  .dynamic-venue__mobile-cta strong {
    color: #edf3ff;
    font-size: 1.12rem;
    letter-spacing: -0.04em;
  }

  .dynamic-venue__mobile-cta button {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 48px;
    padding: 0 10px 0 18px;
    border: 0;
    border-radius: 999px;
    color: #eef3ff;
    font-weight: 900;
    letter-spacing: -0.02em;
    background: rgba(10, 10, 14, 0.18);
    box-shadow: 0 18px 34px rgba(2, 4, 10, 0.28), 0 10px 18px rgba(83, 60, 120, 0.08);
    backdrop-filter: blur(24px) saturate(135%);
  }

  .dynamic-venue__mobile-cta button::before,
  .dynamic-venue__mobile-cta button::after {
    content: '';
    position: absolute;
    border-radius: inherit;
    pointer-events: none;
  }

  .dynamic-venue__mobile-cta button::before {
    inset: 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.06) 18%, rgba(255, 255, 255, 0.02) 48%, rgba(126, 104, 162, 0.04) 100%);
    opacity: 0.32;
  }

  .dynamic-venue__mobile-cta button::after {
    inset: 1px;
    background:
      radial-gradient(circle at 24% -12%, rgba(255, 255, 255, 0.18), transparent 34%),
      radial-gradient(circle at 84% 50%, rgba(120, 91, 163, 0.12), transparent 24%),
      linear-gradient(180deg, rgba(34, 36, 46, 0.32), rgba(10, 10, 16, 0.56));
    opacity: 0.82;
  }

  .dynamic-venue__mobile-cta button span,
  .dynamic-venue__mobile-cta button i {
    position: relative;
    z-index: 1;
  }

  .dynamic-venue__mobile-cta button i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    color: #f4f7fb;
    background:
      radial-gradient(circle at 30% 24%, rgba(255, 255, 255, 0.24), transparent 34%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.12), rgba(142, 107, 196, 0.18));
    border: 1px solid rgba(255, 255, 255, 0.14);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 8px 18px rgba(4, 6, 12, 0.16);
  }

  .dynamic-venue__summary {
    display: none;
  }
}

@media (max-width: 380px) {
  .dynamic-venue__stage,
  .dynamic-venue__svg {
    min-height: clamp(420px, 62svh, 500px);
    height: clamp(420px, 62svh, 500px);
  }

  .dynamic-venue__mobile-cta {
    grid-template-columns: 1fr;
  }

  .dynamic-venue__mobile-cta button {
    width: 100%;
  }

  .dynamic-venue.has-zone-picker.has-selection {
    padding-bottom: 286px;
  }

  .dynamic-venue.has-selection .dynamic-venue__zone-picker {
    bottom: max(14px, calc(env(safe-area-inset-bottom) + 14px));
  }
}
</style>
